import React, { useState, useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import {
  C,
  GlowBtn,
  GlassPanel,
  PlayerBadge,
  StatusBadge,
  NeonText,
  Stars,
  Particles,
  SidePanel,
  ProcessingTimeline,
  useBreakpoint,
} from './ui'
import { useKiosk } from './state/kiosk-context'
import { KVKK_AYDINLATMA_METNI } from './kvkkAydinlatmaMetni'
import { useNearViewport } from './useNearViewport'
import {
  checkFrame,
  fetchVictories,
  type FrameVerdict,
  type VictoryEntry,
  type VictoryOutcome,
} from './api/client'
import { useCamera, type Region } from './camera/useCamera'
import { SELECTABLE_OUTFITS, outfitStyle, type OutfitId } from './outfits'
import { BodySprite, bodyArtUrl, hasBodyArt } from './BodySprite'
import type { ShowcaseCharacter, ShowcaseHead } from './api/client'
import QRCode from 'qrcode'

export type KioskScreen = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

/**
 * Karakter figürü. Formanın oyundaki sanatı varsa **gerçek gövde** kullanılır (BodySprite);
 * yoksa çizili gövdeye düşülür. Kafa her iki durumda da aynı pivot kuralıyla oturur.
 *
 * Yedek bugün yalnız **kontrat dışı kalmış eski kayıtlar** (ya da `outfitId` yokluğu) için
 * çalışır: enum'daki beş formanın (FB·GS·BJK·TS·MILLI) hepsinin sanatı var. Burada eskiden
 * "NEUTRAL/MILLI" yazıyordu; NEUTRAL enum'dan çıktı (#27), MILLI'nin sanatı üretildi (#120).
 */
function CharacterFigure({
  outfitId,
  color,
  headUrl,
  head,
  size,
  fill = false,
}: {
  outfitId: string | null
  color: string
  headUrl: string | null
  /** Kafanın yerleşim sayıları (ADR-018) — yoksa referans değerlere düşülür. */
  head?: ShowcaseHead | undefined
  size: number
  fill?: boolean
}) {
  if (outfitId && hasBodyArt(outfitId)) {
    return (
      <BodySprite
        outfitId={outfitId}
        headUrl={headUrl}
        head={head}
        size={size}
        glowColor={color}
        fill={fill}
      />
    )
  }
  return <FootballCharacterSVG color={color} size={size} headReady headUrl={headUrl} />
}

// ─── Koleksiyon girdisi ───────────────────────────────────────────────────────
// Kart verisi sunucudan gelir: `GET /api/events/{id}/characters` + SSE (docs/api.md).
// Mock'un kendi kendine dolan sahte duvarı kaldırıldı; renk formadan türer.

/** Kartın oynattığı döngü — yalnız görsel çeşitlilik, veriye bağlı değil. */
type CharAnim = 'idle' | 'wave' | 'dance' | 'jump' | 'celebrate'

interface MosaicEntry {
  uid: string
  outfitId: string
  color: string
  anim: CharAnim
  playerNum: string
  playerName: string
  /** Üretilmiş kafa sprite'ının public URL'i (`previewUrl`); yoksa çizili kafa gösterilir. */
  headUrl: string | null
  /** Kafayı boyna oturtan sayılar; sprite boyu ve pivot oranı karakter başına (ADR-018). */
  head?: ShowcaseHead | undefined
  processing?: boolean
  /** Victory Gallery aggregate'leri -- backend henüz göndermiyor, hepsi optional
   * (feature/victory-gallery, 2026-08-29). Bkz. api/client.ts ShowcaseCharacter.
   * `exactOptionalPropertyTypes` açık -- `| undefined` şart, `head` alanındaki desenin aynısı. */
  matchCount?: number | undefined
  winCount?: number | undefined
  lossCount?: number | undefined
  victoryCardCount?: number | undefined
}

const MOSAIC_ANIM_POOL: CharAnim[] = [
  'idle',
  'wave',
  'dance',
  'jump',
  'idle',
  'dance',
  'wave',
  'jump',
  'idle',
  'jump',
]

const ANIM_CSS: Record<CharAnim, string> = {
  idle: 'idle-breathe 3s ease-in-out infinite',
  wave: 'char-wave 1.8s ease-in-out infinite',
  dance: 'char-dance 1.2s ease-in-out infinite',
  jump: 'char-jump 1.6s ease-in-out infinite',
  celebrate: 'celebrate-bounce 0.8s ease-in-out infinite',
}

function entryFromCharacter(c: ShowcaseCharacter, index: number): MosaicEntry {
  const style = outfitStyle(c.outfitId)
  return {
    uid: c.characterId,
    outfitId: c.outfitId,
    color: style.color,
    anim: MOSAIC_ANIM_POOL[index % MOSAIC_ANIM_POOL.length]!,
    playerNum: `#${c.characterId.slice(-3)}`,
    playerName: c.displayName,
    headUrl: c.previewUrl,
    head: c.head,
    processing: c.status !== 'READY',
    matchCount: c.matchCount,
    winCount: c.winCount,
    lossCount: c.lossCount,
    victoryCardCount: c.victoryCardCount,
  }
}

// ─── Character Collection Card ────────────────────────────────────────────────
const ANIM_LABELS: Partial<Record<CharAnim, string>> = {
  idle: 'IDLE BOUNCE',
  wave: 'WAVE',
  celebrate: 'CELEBRATE',
  dance: 'CELEBRATE',
  jump: 'IDLE BOUNCE',
}

// Portrait silhouette used in processing cards
function ProcessingSilhouette() {
  return (
    <svg
      viewBox="0 0 80 100"
      width="70%"
      style={{ maxWidth: '86px', display: 'block', opacity: 0.55 }}
    >
      <defs>
        <linearGradient id="shimmerGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8B5CFF" stopOpacity="0.15" />
          <stop offset="45%" stopColor="#8B5CFF" stopOpacity="0.6" />
          <stop offset="55%" stopColor="#B388FF" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#8B5CFF" stopOpacity="0.15" />
        </linearGradient>
        <mask id="silMask">
          {/* Head */}
          <ellipse cx="40" cy="22" rx="18" ry="20" fill="white" />
          {/* Neck */}
          <rect x="35" y="40" width="10" height="8" fill="white" />
          {/* Shoulders / torso top */}
          <path d="M12 56 Q18 44 40 48 Q62 44 68 56 L68 62 Q40 58 12 62 Z" fill="white" />
        </mask>
        <clipPath id="silClip">
          <ellipse cx="40" cy="22" rx="18" ry="20" />
          <rect x="35" y="40" width="10" height="8" />
          <path d="M12 56 Q18 44 40 48 Q62 44 68 56 L68 62 Q40 58 12 62 Z" />
        </clipPath>
      </defs>
      {/* Base silhouette fill */}
      <ellipse cx="40" cy="22" rx="18" ry="20" fill="#6D3FCC" opacity="0.7" />
      <rect x="35" y="40" width="10" height="8" fill="#6D3FCC" opacity="0.7" />
      <path
        d="M12 56 Q18 44 40 48 Q62 44 68 56 L68 62 Q40 58 12 62 Z"
        fill="#6D3FCC"
        opacity="0.7"
      />
      {/* Shimmer sweep — uses CSS animation via inline style */}
      <rect
        x="0"
        y="0"
        width="80"
        height="100"
        fill="url(#shimmerGrad)"
        mask="url(#silMask)"
        style={{ animation: 'scan-line-vert 1.8s ease-in-out infinite' }}
      />
    </svg>
  )
}

// Animation cycle for the ready card: idle → wave → idle → celebrate → repeat
const READY_ANIM_CYCLE: Array<{ anim: CharAnim; ms: number }> = [
  { anim: 'idle', ms: 4000 },
  { anim: 'wave', ms: 2200 },
  { anim: 'idle', ms: 3200 },
  { anim: 'celebrate', ms: 1600 },
]

/** Onay kendi kendine kapanır: misafir dokunup gidince kart açık onayla kalmasın (#94). */
const DELETE_CONFIRM_MS = 6000
const DELETE_FAILED_MS = 2200

function CollectionCard({
  entry,
  isNewest,
  tileIndex,
  highlighted = false,
  scrollRoot,
  onDelete,
  onOpenDetail,
}: {
  entry: MosaicEntry
  isNewest: boolean
  tileIndex: number
  highlighted?: boolean
  /** Izgaranın kaydırma kabı: kart ona yaklaşınca kafasını ister (#147). */
  scrollRoot: Element | null
  /** Misafirin silme talebi; `false` = sunucu silmedi, kart geri geldi (#94). */
  onDelete?: () => Promise<boolean>
  /** Character Detail / Victory Gallery modalını açar (feature/victory-gallery, 2026-08-29).
   * Yalnız kart HAZIR iken aktif -- üretim sürerken tıklanacak bir geçmiş yok. */
  onOpenDetail?: () => void
}) {
  const [visible, setVisible] = useState(false)
  // Koleksiyon 200 karta kadar çizer; görünmeyen kart kafasını istemez (#147)
  const [cardRef, nearViewport] = useNearViewport<HTMLDivElement>(scrollRoot)
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteFailed, setDeleteFailed] = useState(false)
  const [justReady, setJustReady] = useState(false)
  const [cycleAnim, setCycleAnim] = useState<CharAnim>('idle')
  const wasProcessing = useRef(entry.processing)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60 + tileIndex * 30)
    return () => clearTimeout(t)
  }, [tileIndex])

  // Animation cycling for ready cards — offset start time per tile so they don't all sync
  useEffect(() => {
    if (entry.processing) return
    let phase = 0
    let timer: ReturnType<typeof setTimeout>
    const initialDelay = (tileIndex % READY_ANIM_CYCLE.length) * 1400

    const schedule = () => {
      const { anim, ms } = READY_ANIM_CYCLE[phase % READY_ANIM_CYCLE.length]!
      setCycleAnim(anim)
      timer = setTimeout(() => {
        phase++
        schedule()
      }, ms)
    }
    const boot = setTimeout(schedule, initialDelay)
    return () => {
      clearTimeout(boot)
      clearTimeout(timer)
    }
  }, [entry.processing, tileIndex])

  // Detect transition from processing → ready and fire reveal animation
  useEffect(() => {
    if (wasProcessing.current && !entry.processing) {
      setJustReady(true)
      const t = setTimeout(() => setJustReady(false), 1200)
      return () => clearTimeout(t)
    }
    wasProcessing.current = entry.processing
  }, [entry.processing])

  useEffect(() => {
    if (!confirming || deleting) return
    const t = setTimeout(() => setConfirming(false), DELETE_CONFIRM_MS)
    return () => clearTimeout(t)
  }, [confirming, deleting])

  useEffect(() => {
    if (!deleteFailed) return
    const t = setTimeout(() => {
      setDeleteFailed(false)
      setConfirming(false)
    }, DELETE_FAILED_MS)
    return () => clearTimeout(t)
  }, [deleteFailed])

  const isProcessing = entry.processing === true
  // Üretimi süren kart silinemez: sunucu da 409 der (worker yazımıyla yarışmasın)
  const canDelete = onDelete !== undefined && !isProcessing

  const confirmDelete = async () => {
    if (!onDelete || deleting) return
    setDeleting(true)
    const ok = await onDelete()
    // Başarılıysa kart gridden düşer ve bu bileşen sökülür; state'e dokunulmaz
    if (!ok) {
      setDeleting(false)
      setDeleteFailed(true)
    }
  }

  const cardBg = isProcessing
    ? 'linear-gradient(165deg, rgba(88,40,200,0.18) 0%, #07050F 60%, rgba(88,40,200,0.08) 100%)'
    : `linear-gradient(165deg, ${entry.color}18 0%, #07050F 60%, ${entry.color}0a 100%)`
  const cardBorder = isProcessing
    ? `rgba(139,92,255,0.35)`
    : isNewest || highlighted
      ? entry.color + 'cc'
      : entry.color + '2a'

  return (
    <div
      ref={cardRef}
      onClick={() => {
        if (!isProcessing) onOpenDetail?.()
      }}
      role={!isProcessing && onOpenDetail ? 'button' : undefined}
      style={{
        aspectRatio: '9/16',
        // Ekran dışındaki kart çizilmez (#147): 200 kartta boşta ana iş parçacığı 5 sn'de 2,5 sn
        // meşguldü, bununla 1,5 sn (headless, medyan n=4). Boy `aspectRatio`'dan gelir; yerleşim
        // ölçüldü, birebir aynı. Tahmini boy yalnız ilk çizim için.
        contentVisibility: 'auto',
        containIntrinsicSize: 'auto 220px',
        cursor: !isProcessing && onOpenDetail ? 'pointer' : 'default',
        // Kart içi ölçüler kartın **genişliğine** oranlı (cqw): sütun sayısı değişince
        // (gridCols) tipografi de kartla büyür, elde güncellenecek piksel kalmaz (#71).
        // Taban 151 px'lik eski kart: 12 px ad = 8cqw. Kabın kendi stiline cqw yazılmaz.
        containerType: 'inline-size',
        position: 'relative',
        overflow: 'hidden',
        // 🚨 Piksel, `cqw` DEĞİL. Bir eleman **kendi** container birimini kullanamaz: `cqw` en
        // yakın ÜST container'a, yoksa viewport'a çözülür. Burada 6.6cqw denendi ve 1920 px'lik
        // ekranda 127 px yarıçap oldu — kart hap şekline girdi ve `overflow: hidden` ile isim,
        // rozet, "HAZIR" satırını kırptı (canlıda görüldü). Kartın **içindeki** ölçüler cqw
        // kullanmaya devam ediyor, onlar doğru çözülüyor.
        borderRadius: '14px',
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        boxShadow: isProcessing
          ? `0 0 18px rgba(139,92,255,0.22), inset 0 0 20px rgba(139,92,255,0.08)`
          : isNewest || highlighted
            ? `0 0 20px ${entry.color}44, inset 0 0 24px ${entry.color}10`
            : `inset 0 0 12px ${entry.color}06`,
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(12px)',
        opacity: visible ? 1 : 0,
        transition:
          'transform 0.45s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease, background 0.6s ease, border-color 0.6s ease, box-shadow 0.6s ease',
      }}
    >
      {/* Arcade grid bg */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: isProcessing ? 0.02 : 0.04,
          pointerEvents: 'none',
          backgroundImage: `linear-gradient(${isProcessing ? C.neonPurple : entry.color} 1px, transparent 1px), linear-gradient(90deg, ${isProcessing ? C.neonPurple : entry.color} 1px, transparent 1px)`,
          backgroundSize: '20px 20px',
        }}
      />

      {/* ── PROCESSING STATE ── */}
      {isProcessing && (
        <>
          {/* Pulsing background aura */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'radial-gradient(ellipse at 50% 40%, rgba(139,92,255,0.18) 0%, transparent 65%)',
              animation: 'glow-pulse 2s ease-in-out infinite',
            }}
          />
          {/* Scan line */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: '2px',
              zIndex: 3,
              background: `linear-gradient(90deg, transparent, ${C.neonPurple}cc 40%, ${C.cyan}99 60%, transparent)`,
              boxShadow: `0 0 8px ${C.neonPurple}`,
              animation: 'scan-line 1.8s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />

          {/* Silhouette */}
          <div
            style={{
              position: 'absolute',
              top: '9.3cqw',
              left: 0,
              right: 0,
              bottom: '30cqw',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              paddingBottom: '4px',
            }}
          >
            <ProcessingSilhouette />
          </div>

          {/* Status pill */}
          <div
            style={{
              position: 'absolute',
              top: '4.6cqw',
              left: '4.6cqw',
              right: '4.6cqw',
              zIndex: 4,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                background: 'rgba(139,92,255,0.2)',
                border: `1px solid ${C.neonPurple}55`,
                borderRadius: '2.6cqw',
                padding: '1.3cqw 5.3cqw',
                fontSize: '4.6cqw',
                fontWeight: 800,
                color: C.neonPurple,
                fontFamily: "'JetBrains Mono',monospace",
                letterSpacing: '0.08em',
                display: 'flex',
                alignItems: 'center',
                gap: '2.6cqw',
                animation: 'glow-pulse 1.6s ease-in-out infinite',
              }}
            >
              OLUŞTURULUYOR
              <span style={{ display: 'inline-flex', gap: '2px' }}>
                {[0, 1, 2].map((j) => (
                  <span
                    key={j}
                    style={{
                      width: '3px',
                      height: '3px',
                      borderRadius: '50%',
                      background: C.neonPurple,
                      display: 'inline-block',
                      animation: `glow-pulse 1s ${j * 0.25}s ease-in-out infinite`,
                    }}
                  />
                ))}
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div
            style={{
              position: 'absolute',
              bottom: '25cqw',
              left: '6.6cqw',
              right: '6.6cqw',
              height: '2px',
              borderRadius: '1px',
              background: 'rgba(139,92,255,0.15)',
              overflow: 'hidden',
              zIndex: 4,
            }}
          >
            <div
              style={{
                height: '100%',
                width: '40%',
                borderRadius: '1px',
                background: `linear-gradient(90deg, ${C.neonPurple}, ${C.cyan})`,
                boxShadow: `0 0 6px ${C.neonPurple}`,
                animation: 'progress-shuttle 1.8s ease-in-out infinite',
              }}
            />
          </div>
        </>
      )}

      {/* ── READY STATE ── */}
      {!isProcessing && (
        <>
          {/* Ambient glow floor */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '50%',
              background: `radial-gradient(ellipse at 50% 100%, ${entry.color}28 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />

          {/* Animation state pill — top left */}
          {(() => {
            const lbl = ANIM_LABELS[cycleAnim] ?? 'IDLE BOUNCE'
            const col = lbl === 'WAVE' ? C.cyan : lbl === 'CELEBRATE' ? C.magenta : C.neonPurple
            return (
              <div
                key={lbl}
                style={{
                  position: 'absolute',
                  top: '4.6cqw',
                  left: '4.6cqw',
                  zIndex: 4,
                  background: `${col}22`,
                  border: `1px solid ${col}44`,
                  borderRadius: '2.6cqw',
                  padding: '1.3cqw 4cqw',
                  fontSize: '4.6cqw',
                  fontWeight: 800,
                  color: col,
                  fontFamily: "'JetBrains Mono',monospace",
                  letterSpacing: '0.08em',
                  animation: 'slide-up 0.25s ease both',
                }}
              >
                {lbl}
              </div>
            )
          })()}

          {/* YENİ / HAZIR badge */}
          {(isNewest || justReady) && (
            <div
              style={{
                position: 'absolute',
                // Sağ üst köşe sil düğmesinin: sol üstte animasyon etiketi duruyor ve düğme onu
                // kapatıyordu (canlıda görüldü). Rozet aynı kenarda bir sıra aşağı indi —
                // düğmenin alt kenarı `minHeight` yüzünden dar kartta ~25cqw'ye kadar iniyor.
                top: '27cqw',
                right: '4.6cqw',
                zIndex: 4,
                background: justReady
                  ? 'linear-gradient(135deg, #00BFA5, #00E676)'
                  : `linear-gradient(135deg, ${C.primaryPurple}, ${C.neonPurple})`,
                borderRadius: '2.6cqw',
                padding: '1.3cqw 4.6cqw',
                fontSize: '4.6cqw',
                fontWeight: 800,
                color: '#fff',
                letterSpacing: '0.1em',
                fontFamily: "'Exo 2',sans-serif",
                boxShadow: justReady ? '0 0 10px rgba(0,230,118,0.7)' : `0 0 8px ${C.neonPurple}88`,
                animation: 'glow-pulse 1.4s ease-in-out infinite',
              }}
            >
              {justReady ? 'HAZIR ✓' : 'YENİ'}
            </div>
          )}

          {/* Character — FootballCharacterSVG with personalized head */}
          <div
            style={{
              position: 'absolute',
              // Tepedeki pay zıplama payıdır: char-jump en çok -%12 + scaleY 1.03 yol alıyor,
              // kart `overflow: hidden` olduğu için pay olmadan figür karttan taşıyordu.
              top: '13%',
              left: '2px',
              right: '2px',
              bottom: '20cqw',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              animation: justReady ? 'slide-up 0.5s ease both' : ANIM_CSS[cycleAnim],
            }}
          >
            <CharacterFigure
              outfitId={entry.outfitId}
              color={entry.color}
              headUrl={nearViewport ? entry.headUrl : null}
              head={entry.head}
              size={82}
              fill
            />
          </div>
        </>
      )}

      {/* Nameplate — always visible */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '8cqw 4cqw 3.3cqw',
          background:
            'linear-gradient(0deg, rgba(5,3,12,0.98) 0%, rgba(5,3,12,0.85) 55%, transparent 100%)',
          zIndex: 5,
        }}
      >
        <div
          style={{
            fontFamily: "'Exo 2',sans-serif",
            fontWeight: 900,
            fontSize: '8cqw',
            color: isProcessing ? `${C.neonPurple}bb` : entry.color,
            textShadow: isProcessing ? `0 0 8px ${C.neonPurple}66` : `0 0 8px ${entry.color}99`,
            letterSpacing: '0.06em',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            transition: 'color 0.5s ease',
          }}
        >
          {entry.playerName.split(' ')[0]}
        </div>
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '5.3cqw',
            color: C.secText,
            marginTop: '1.3cqw',
            letterSpacing: '0.06em',
          }}
        >
          PLAYER {entry.playerNum}
        </div>
        {/* Galibiyet rozeti -- yalnız backend aggregate gönderirse (feature/victory-gallery,
            2026-08-29). Alan yoksa hiçbir şey basılmaz; 0 varsayılmaz (ADR-009 ruhu). */}
        {entry.winCount !== undefined && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.3cqw',
              marginTop: '1.3cqw',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '4.6cqw',
              fontWeight: 800,
              color: '#FFC53D',
              letterSpacing: '0.06em',
            }}
          >
            🏆 {entry.winCount} GALİBİYET
          </div>
        )}
        {/* Status dot */}
        {!isProcessing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '2.6cqw', marginTop: '3px' }}>
            <div
              style={{
                width: '3.3cqw',
                height: '3.3cqw',
                borderRadius: '50%',
                background: '#00E676',
                boxShadow: '0 0 5px #00E676',
                animation: 'glow-pulse 2s ease-in-out infinite',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '4.6cqw',
                color: '#00E676',
                letterSpacing: '0.1em',
                fontWeight: 700,
              }}
            >
              HAZIR
            </span>
          </div>
        )}
      </div>

      {/* Sil — misafir kendini beğenmediyse (#94). Onay olmadan silinmez: geri dönüşü yok ve
          yeniden üretim paralı bir çağrı. Sağ üstte: sol üst köşede animasyon etiketi var. */}
      {canDelete && !confirming && (
        <button
          type="button"
          aria-label={`${entry.playerName} karakterini sil`}
          onClick={(e) => {
            e.stopPropagation()
            setConfirming(true)
          }}
          style={{
            position: 'absolute',
            top: '4.6cqw',
            right: '4.6cqw',
            zIndex: 6,
            width: '16cqw',
            height: '16cqw',
            minWidth: '36px',
            minHeight: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            border: `1px solid ${C.secText}55`,
            background: 'rgba(5,3,12,0.72)',
            color: C.secText,
            fontSize: '7cqw',
            lineHeight: 1,
            padding: 0,
            cursor: 'pointer',
          }}
        >
          🗑
        </button>
      )}

      {confirming && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 7,
            background: 'rgba(5,3,12,0.94)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4cqw',
            padding: '5cqw',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: '9cqw',
              lineHeight: 1.15,
              color: deleteFailed ? C.magenta : C.white,
            }}
          >
            {deleteFailed ? 'Silinemedi' : 'Silinsin mi?'}
          </div>
          {!deleteFailed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3cqw', width: '100%' }}>
              <button
                type="button"
                onClick={() => void confirmDelete()}
                disabled={deleting}
                style={{
                  fontFamily: "'Exo 2',sans-serif",
                  fontWeight: 900,
                  fontSize: '7cqw',
                  letterSpacing: '0.08em',
                  color: C.white,
                  background: `linear-gradient(135deg, ${C.magenta}, #C2185B)`,
                  border: 'none',
                  borderRadius: '3cqw',
                  padding: '3.5cqw 0',
                  minHeight: '34px',
                  cursor: 'pointer',
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? '…' : 'SİL'}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={deleting}
                style={{
                  fontFamily: "'Exo 2',sans-serif",
                  fontWeight: 800,
                  fontSize: '7cqw',
                  letterSpacing: '0.08em',
                  color: C.secText,
                  background: 'transparent',
                  border: `1px solid ${C.secText}55`,
                  borderRadius: '3cqw',
                  padding: '3.5cqw 0',
                  minHeight: '34px',
                  cursor: 'pointer',
                }}
              >
                VAZGEÇ
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Collection Grid ──────────────────────────────────────────────────────────
function CollectionGrid({ cols }: { cols: number }) {
  const { collection, newestCharacterId, removeCharacter } = useKiosk()
  const entries = useMemo(() => collection.map(entryFromCharacter), [collection])
  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [detailEntry, setDetailEntry] = useState<MosaicEntry | null>(null)
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null)

  const q = query.trim().toLowerCase()
  const filtered = q
    ? entries.filter(
        (e) => e.playerName.toLowerCase().includes(q) || e.playerNum.toLowerCase().includes(q),
      )
    : entries

  const openSearch = () => {
    setSearchOpen(true)
    setTimeout(() => inputRef.current?.focus(), 60)
  }
  const closeSearch = () => {
    setSearchOpen(false)
    setQuery('')
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '7px 10px',
          background: 'rgba(5,3,12,0.9)',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${searchOpen ? C.neonPurple + '44' : 'rgba(139,92,255,0.1)'}`,
          transition: 'border-color 0.2s ease',
        }}
      >
        {searchOpen ? (
          <>
            <span style={{ color: C.neonPurple, fontSize: '14px', flexShrink: 0 }}>🔍</span>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="İsim veya oyuncu no ara…"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: C.white,
                fontSize: '13px',
                fontFamily: "'Exo 2',sans-serif",
                fontWeight: 600,
                caretColor: C.neonPurple,
              }}
            />
            {query && (
              <span
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: '9px',
                  color: C.neonPurple,
                  letterSpacing: '0.06em',
                  flexShrink: 0,
                }}
              >
                {filtered.length} sonuç
              </span>
            )}
            <button
              onClick={closeSearch}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: C.secText,
                fontSize: '15px',
                padding: '2px 4px',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </>
        ) : (
          <>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '9px',
                color: `${C.secText}88`,
                letterSpacing: '0.1em',
                flex: 1,
              }}
            >
              {entries.length} KARAKTER
            </div>
            <button
              onClick={openSearch}
              style={{
                background: 'rgba(139,92,255,0.1)',
                border: '1px solid rgba(139,92,255,0.25)',
                borderRadius: '7px',
                cursor: 'pointer',
                color: C.secText,
                fontSize: '11px',
                fontFamily: "'Exo 2',sans-serif",
                fontWeight: 700,
                padding: '4px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              🔍 KARAKTERİ BUL
            </button>
          </>
        )}
      </div>

      {/* Grid */}
      <div ref={setScrollRoot} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {filtered.length === 0 && q ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              gap: '10px',
              color: C.secText,
            }}
          >
            <div style={{ fontSize: '28px' }}>🕹️</div>
            <div
              style={{
                fontFamily: "'Exo 2',sans-serif",
                fontWeight: 700,
                fontSize: '13px',
                letterSpacing: '0.08em',
              }}
            >
              Karakter bulunamadı
            </div>
            <div style={{ fontSize: '11px', opacity: 0.55 }}>"{query}" ile eşleşen yok</div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: '6px',
              padding: '8px',
            }}
          >
            {filtered.map((entry, i) => (
              <CollectionCard
                key={entry.uid}
                entry={entry}
                onDelete={() => removeCharacter(entry.uid)}
                onOpenDetail={() => setDetailEntry(entry)}
                isNewest={
                  !q &&
                  (newestCharacterId ? entry.uid === newestCharacterId : i === entries.length - 1)
                }
                tileIndex={entries.indexOf(entry)}
                highlighted={q.length > 0}
                scrollRoot={scrollRoot}
              />
            ))}
          </div>
        )}
      </div>

      {detailEntry &&
        createPortal(
          // Portal to <body> -- this component is otherwise mounted deep inside the RIGHT
          // grid column's own `position:relative; zIndex:2` wrapper (see CollectionScreen's
          // two-column grid), which forms its own stacking context. A position:fixed
          // descendant's zIndex (200 here) only ever competes within its nearest ANCESTOR
          // stacking context -- it can't out-rank a completely separate sibling context, no
          // matter how high the number. The LEFT panel (`zIndex: 5`, its own
          // position:relative context) sits in that same outer sibling slot, so its whole
          // subtree painted ABOVE this modal's entire subtree regardless of the 200 -- name/
          // stats/avatar got visually clipped under the left panel whenever the centered
          // modal box's left edge fell under it (reported live, screenshot: "urçii" name and
          // stats half-hidden behind the CHARGEN sidebar). Portaling to `document.body`
          // removes the DOM nesting (React context/events still work through the portal),
          // so `zIndex: 200` finally competes directly against the sidebar's `zIndex: 5` in
          // the shared root stacking context and wins, covering the whole screen as the
          // `position:fixed; inset:0` was always meant to. FullPosterModal (`zIndex: 300`,
          // opened from inside this modal) travels along for free -- it's nested in the
          // same portaled subtree.
          <CharacterDetailModal entry={detailEntry} onClose={() => setDetailEntry(null)} />,
          document.body,
        )}
    </div>
  )
}

// ─── Character Detail / Victory Gallery Modal (feature/victory-gallery, 2026-08-29) ──────────
// Kart üretiminin kendisine hiç dokunmuyor: Unity maç bitince POST /api/matches/ended atmaya,
// chargen kartı arka planda otomatik üretmeye devam ediyor (ADR/mimari kararı, chat 2026-08-29).
// Bu modal yalnız GET /api/characters/{id}/victories'i okuyup gösteriyor -- salt okunur bir
// vitrin. Telefon tarafındaki akış artık burayı işaret ediyor (mobile.tsx sadeleşti).

// Backend'in Outcome StrEnum'ıyla birebir -- canlı veriyle doğrulandı (2026-08-29): "LOSS",
// "lose" değil.
const OUTCOME_LABEL: Record<VictoryOutcome, string> = {
  WIN: 'GALİBİYET',
  LOSS: 'MAĞLUBİYET',
  DRAW: 'BERABERE',
}
const OUTCOME_COLOR: Record<VictoryOutcome, string> = {
  WIN: '#00E676',
  LOSS: '#F044C7',
  DRAW: '#F5A623',
}

function CharacterDetailModal({ entry, onClose }: { entry: MosaicEntry; onClose: () => void }) {
  const [victories, setVictories] = useState<VictoryEntry[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [openVictory, setOpenVictory] = useState<VictoryEntry | null>(null)

  useEffect(() => {
    // Reset olmadan doğrudan fetch: bu component her `entry` için TEK MODAL AÇILIŞINDA bir kez
    // mount oluyor (kapatılınca unmount, tekrar açılınca fresh instance) -- victories/loadError
    // zaten başlangıç değerleriyle (null/false) mount oluyor, effect içinde senkron setState'e
    // gerek yok (react-hooks/set-state-in-effect).
    const controller = new AbortController()
    fetchVictories(entry.uid, 50, controller.signal)
      .then(setVictories)
      .catch((err) => {
        if ((err as { name?: string }).name === 'AbortError') return
        setLoadError(true)
      })
    return () => controller.abort()
  }, [entry.uid])

  // Backend aggregate'leri varsa onları kullan; yoksa (bugünkü durum) çekilen victories
  // listesinden türet -- ikisi de aynı sayıyı verir, aggregate gelince fazladan bir istek
  // atmadan (N+1 yok) doğrudan kartta göstermeye hazır olur.
  // Backend'in `_victory_stats` aggregate'iyle aynı tanım (feature/victory-gallery, 2026-08-29):
  // maç sonucu poster üretiminden bağımsız gerçektir, wins/losses de matches gibi TÜM
  // victory_posters satırlarını sayar -- yalnız READY olanları değil. Backend aggregate gelince
  // sayı değişmesin diye fallback aynı tanımı taklit ediyor.
  const all = victories ?? []
  const wins = entry.winCount ?? all.filter((v) => v.outcome === 'WIN').length
  const losses = entry.lossCount ?? all.filter((v) => v.outcome === 'LOSS').length
  const matches = entry.matchCount ?? victories?.length

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(5,3,12,0.92)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        animation: 'depth-in 0.2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '760px',
          maxHeight: '86vh',
          overflowY: 'auto',
          background: `linear-gradient(165deg, ${entry.color}14 0%, #0A0716 55%, #07050F 100%)`,
          border: `1px solid ${entry.color}44`,
          borderRadius: '22px',
          boxShadow: `0 0 60px ${entry.color}22, 0 20px 60px rgba(0,0,0,0.6)`,
          padding: '28px',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Kapat"
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.16)',
            color: '#fff',
            fontSize: '17px',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>

        {/* Header — avatar + identity + stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div
            style={{
              width: '96px',
              height: '96px',
              borderRadius: '50%',
              flexShrink: 0,
              background: `radial-gradient(circle at 35% 35%, ${entry.color}55, #07050F)`,
              border: `2.5px solid ${entry.color}99`,
              overflow: 'hidden',
            }}
          >
            {/* Gövde yerine yalnız yüz: tüm figürü 96px daireye sığdırmak vücudu küçültüp
                yüzü kayboluyordu -- baş görseli zaten yüz odaklı üretiliyor, dolayısıyla
                cover+merkez bu boyutta doğrudan yüz gösterir. */}
            {entry.headUrl ? (
              <img
                src={entry.headUrl}
                alt={entry.playerName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  transform: 'scale(1.18)',
                }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CharacterFigure
                  outfitId={entry.outfitId}
                  color={entry.color}
                  headUrl={entry.headUrl}
                  head={entry.head}
                  size={72}
                />
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "'Exo 2',sans-serif",
                fontWeight: 900,
                fontSize: '26px',
                color: entry.color,
                letterSpacing: '0.04em',
              }}
            >
              {entry.playerName}
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '12px',
                color: C.secText,
                marginTop: '4px',
                letterSpacing: '0.06em',
              }}
            >
              PLAYER {entry.playerNum}
            </div>
            <div style={{ display: 'flex', gap: '18px', marginTop: '10px' }}>
              <Stat label="MAÇ" value={matches} />
              <Stat label="GALİBİYET" value={wins} color="#00E676" />
              <Stat label="MAĞLUBİYET" value={losses} color="#F044C7" />
            </div>
          </div>
        </div>

        {/* Victory gallery */}
        <div style={{ marginTop: '26px' }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '11px',
              fontWeight: 800,
              color: C.secText,
              letterSpacing: '0.16em',
              marginBottom: '12px',
            }}
          >
            ZAFER KARTLARI
          </div>

          {victories === null && !loadError && (
            <div
              style={{ padding: '30px 0', textAlign: 'center', color: C.secText, fontSize: '13px' }}
            >
              Yükleniyor…
            </div>
          )}
          {loadError && (
            <div
              style={{ padding: '30px 0', textAlign: 'center', color: '#F044C7', fontSize: '13px' }}
            >
              Kartlar yüklenemedi.
            </div>
          )}
          {victories !== null && victories.length === 0 && (
            <div
              style={{ padding: '30px 0', textAlign: 'center', color: C.secText, fontSize: '13px' }}
            >
              Henüz bir maç oynanmamış.
            </div>
          )}
          {victories !== null && victories.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                gap: '10px',
              }}
            >
              {victories.map((v) => (
                <VictoryThumb key={v.victoryId} victory={v} onOpen={() => setOpenVictory(v)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {openVictory && (
        <FullPosterModal victory={openVictory} onClose={() => setOpenVictory(null)} />
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  color,
}: {
  label: string
  value: number | undefined
  color?: string
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'Exo 2',sans-serif",
          fontWeight: 900,
          fontSize: '20px',
          color: color ?? '#fff',
          lineHeight: 1,
        }}
      >
        {value ?? '—'}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: '9px',
          color: C.secText,
          letterSpacing: '0.1em',
          marginTop: '3px',
        }}
      >
        {label}
      </div>
    </div>
  )
}

function VictoryThumb({ victory, onOpen }: { victory: VictoryEntry; onOpen: () => void }) {
  const ready = victory.status === 'READY' && victory.posterUrl
  const [thumbMissing, setThumbMissing] = useState(false)
  // Izgara yalnız küçük resmi çizer (#147): yoksa yer tutucu, postere düşmez — düşseydi eksik
  // türevli profilde kart başına 1–2 MB'lık PNG topluca inerdi. Kart yine açılır; tek görselin
  // (modal) orijinale düşmesi `poster.webp` ucunda.
  const showThumb = ready && victory.thumbUrl && !thumbMissing
  return (
    <button
      type="button"
      onClick={ready ? onOpen : undefined}
      disabled={!ready}
      style={{
        aspectRatio: '9/16',
        borderRadius: '10px',
        border: `1px solid ${OUTCOME_COLOR[victory.outcome]}44`,
        background: ready ? '#0A0716' : 'rgba(255,255,255,0.03)',
        padding: 0,
        overflow: 'hidden',
        cursor: ready ? 'pointer' : 'default',
        position: 'relative',
      }}
    >
      {showThumb ? (
        <img
          src={victory.thumbUrl}
          alt={victory.headline}
          loading="lazy"
          decoding="async"
          onError={() => setThumbMissing(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontFamily: "'JetBrains Mono',monospace",
            color: C.secText,
            textAlign: 'center',
            padding: '4px',
          }}
        >
          {victory.status === 'FAILED' ? 'Oluşturulamadı' : 'Hazırlanıyor…'}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '3px 5px',
          background: 'rgba(5,3,12,0.85)',
          fontSize: '8px',
          fontWeight: 800,
          fontFamily: "'JetBrains Mono',monospace",
          color: OUTCOME_COLOR[victory.outcome],
          letterSpacing: '0.06em',
        }}
      >
        {OUTCOME_LABEL[victory.outcome]}
      </div>
    </button>
  )
}

function FullPosterModal({ victory, onClose }: { victory: VictoryEntry; onClose: () => void }) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!victory.posterUrl) return
    let cancelled = false
    // MVP: mevcut posterUrl zaten kalıcı/public (victoryId tahmin edilemez, immutable cache,
    // #41) -- kısa rotaya gerek yok, doğrudan bunu QR'a gömüyoruz (docs/api.md).
    QRCode.toDataURL(victory.posterUrl, {
      margin: 1,
      width: 240,
      color: { dark: '#0A0716', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [victory.posterUrl])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(2,1,6,0.96)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          gap: '28px',
          alignItems: 'center',
          maxWidth: '820px',
          width: '100%',
        }}
      >
        <img
          // Ekranda WebP; QR'daki bağlantı (aşağıda) PNG kalır -- paylaşılan dosya o (#139)
          src={victory.posterWebpUrl ?? victory.posterUrl}
          alt={victory.headline}
          style={{
            maxHeight: '78vh',
            borderRadius: '16px',
            boxShadow: `0 0 50px ${OUTCOME_COLOR[victory.outcome]}33, 0 20px 60px rgba(0,0,0,0.7)`,
            objectFit: 'contain',
          }}
        />
        <div style={{ minWidth: '220px', maxWidth: '260px' }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '11px',
              fontWeight: 800,
              color: OUTCOME_COLOR[victory.outcome],
              letterSpacing: '0.12em',
            }}
          >
            {OUTCOME_LABEL[victory.outcome]}
          </div>
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: '22px',
              color: '#fff',
              marginTop: '8px',
              lineHeight: 1.2,
            }}
          >
            {victory.headline}
          </div>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '12px',
              color: C.secText,
              marginTop: '8px',
              lineHeight: 1.5,
            }}
          >
            {victory.caption}
          </div>

          {qrDataUrl && (
            <div style={{ marginTop: '22px' }}>
              <img
                src={qrDataUrl}
                alt="QR ile al"
                style={{ width: '140px', height: '140px', borderRadius: '8px' }}
              />
              <div
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: '10px',
                  color: C.secText,
                  marginTop: '8px',
                  letterSpacing: '0.06em',
                }}
              >
                QR İLE AL
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            style={{
              marginTop: '22px',
              width: '100%',
              height: '42px',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'transparent',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              cursor: 'pointer',
            }}
          >
            KAPAT
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Spotlight Overlay ────────────────────────────────────────────────────────
function SpotlightOverlay({ entry, onDone }: { entry: MosaicEntry; onDone: () => void }) {
  const [phase, setPhase] = useState<'in' | 'hold' | 'out'>('in')

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('hold'), 300)
    const t2 = setTimeout(() => setPhase('out'), 2700)
    const t3 = setTimeout(() => onDone(), 3400)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [onDone])

  const isOut = phase === 'out'

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Backdrop
        background: 'rgba(3,1,12,0.82)',
        backdropFilter: 'blur(3px)',
        opacity: isOut ? 0 : 1,
        transition: 'opacity 0.5s ease',
        pointerEvents: isOut ? 'none' : 'auto',
      }}
    >
      {/* Ambient particles */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <Particles count={22} color={C.neonPurple} />
        <Particles count={10} color={C.magenta} />
      </div>

      {/* Confetti burst */}
      {Array.from({ length: 32 }, (_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${35 + (i % 7) * 5}%`,
            top: `${20 + (i % 5) * 4}%`,
            width: `${5 + (i % 5)}px`,
            height: `${5 + (i % 6)}px`,
            background: ['#8B5CFF', '#35D8FF', '#F044C7', '#FFD700', '#00E676'][i % 5],
            borderRadius: i % 3 === 0 ? '50%' : '2px',
            ['--spin' as string]: `${i % 2 === 0 ? '' : '-'}${200 + i * 18}deg`,
            animation: `confetti-fall ${1.8 + (i % 4) * 0.3}s ${(i % 6) * 0.08}s ease forwards`,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Center stage */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          transform: isOut
            ? 'scale(0.4) translate(35%, 35%)'
            : phase === 'in'
              ? 'scale(0.7)'
              : 'scale(1)',
          opacity: phase === 'in' ? 0 : 1,
          transition: isOut
            ? 'transform 0.55s cubic-bezier(0.4,0,1,1), opacity 0.4s ease'
            : 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "'Exo 2',sans-serif",
            fontWeight: 900,
            fontSize: 'clamp(14px,2.4vw,22px)',
            color: C.white,
            letterSpacing: '0.12em',
            textAlign: 'center',
            textShadow: `0 0 20px ${C.neonPurple}`,
            animation: 'glow-pulse 2s ease-in-out infinite',
          }}
        >
          YENİ OYUNCU KATILDI!
        </div>

        {/* Neon ring + character */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Outer pulse rings */}
          {[0, 1].map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: `${200 + i * 60}px`,
                height: `${200 + i * 60}px`,
                borderRadius: '50%',
                border: `${2 - i * 0.5}px solid ${i === 0 ? C.neonPurple : C.magenta}`,
                opacity: 0.35 - i * 0.1,
                animation: `ring-expand ${1.4 + i * 0.5}s ${i * 0.3}s ease-out infinite`,
              }}
            />
          ))}

          {/* Solid neon ring */}
          <div
            style={{
              position: 'absolute',
              width: '170px',
              height: '170px',
              borderRadius: '50%',
              border: `2px solid ${C.neonPurple}`,
              boxShadow: `0 0 20px ${C.neonPurple}88, inset 0 0 20px ${C.neonPurple}22`,
              background: `radial-gradient(circle, ${C.neonPurple}18 0%, transparent 70%)`,
            }}
          />

          {/* Character — celebrate loop */}
          <div
            style={{
              animation: 'celebrate-bounce 0.85s ease-in-out infinite',
              position: 'relative',
              zIndex: 3,
            }}
          >
            <CharacterFigure
              outfitId={entry.outfitId}
              color={entry.color}
              headUrl={entry.headUrl}
              head={entry.head}
              size={clamp(130, 160)}
            />
          </div>

          {/* Glow floor */}
          <div
            style={{
              position: 'absolute',
              bottom: '-10px',
              width: '140px',
              height: '22px',
              borderRadius: '50%',
              background: `radial-gradient(ellipse, ${entry.color}66 0%, transparent 70%)`,
              animation: 'platform-glow 2s ease-in-out infinite',
              ['--gc' as string]: entry.color,
            }}
          />
        </div>

        {/* Name + status */}
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: 'clamp(20px,3vw,32px)',
              color: entry.color,
              textShadow: `0 0 14px ${entry.color}`,
              letterSpacing: '0.08em',
            }}
          >
            {entry.playerName.split(' ')[0]}
          </div>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '13px',
              color: C.secText,
              letterSpacing: '0.12em',
            }}
          >
            PLAYER {entry.playerNum}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '2px',
              padding: '5px 14px',
              borderRadius: '999px',
              background: 'rgba(0,230,118,0.15)',
              border: '1px solid rgba(0,230,118,0.4)',
            }}
          >
            <div
              style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: '#00E676',
                boxShadow: '0 0 6px #00E676',
                animation: 'glow-pulse 1.2s ease-in-out infinite',
              }}
            />
            <span
              style={{
                fontFamily: "'Exo 2',sans-serif",
                fontWeight: 800,
                fontSize: '12px',
                color: '#00E676',
                letterSpacing: '0.1em',
              }}
            >
              KARAKTER HAZIR
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// tiny helper — clamp a value without the Math import noise
function clamp(a: number, b: number) {
  return Math.max(a, Math.min(b, b))
}

// ─── Screen 01 — Attract Mode / Character Collection ─────────────────────────
function Screen01({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { collection, newestCharacterId } = useKiosk()
  const [dismissedSpotlight, setDismissedSpotlight] = useState<string | null>(null)

  const entries = useMemo(() => collection.map(entryFromCharacter), [collection])
  // `updated_at` artan geldiği için son eleman en yeni karakterdir (docs/api.md)
  const latestEntry = entries.length > 0 ? entries[entries.length - 1]! : null
  // Spotlight yalnız SSE ile **az önce** hazır olan karakter için açılır, bir kez
  const spotlightEntry =
    newestCharacterId && newestCharacterId !== dismissedSpotlight
      ? (entries.find((e) => e.uid === newestCharacterId) ?? null)
      : null

  // Kart `aspectRatio: 9/16` ve `1fr` — boyutunu **yalnız** sütun sayısı belirler. 10/7/4
  // iken masaüstünde kart ~151 px kalıyordu; misafir uzaktan kendini seçemiyordu (#71).
  // 7/5 → 6/4 (#95). Tarayıcıda ölçüldü: 1600 px'te kart 172→202 px (**+%17**), 1000 px'te
  // 136→172 px (**+%26**). Mobil 3'te kalıyor: 2 sütun kartı telefonda gereksiz devleştirir.
  // Masaüstü 6 → 4 (kullanıcı kararı, canlı etkinlik: 6 sütunda misafir karakterin ne olduğunu
  // uzaktan seçemedi). Tablet zaten 4'tü; ≥768 px'te artık tek sayı.
  const gridCols = bp.isMobile ? 3 : 4

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        background: C.deepBlack,
        display: bp.isMobile ? 'flex' : 'grid',
        flexDirection: bp.isMobile ? 'column' : undefined,
        gridTemplateColumns: bp.isMobile
          ? undefined
          : bp.isTablet
            ? '280px 1fr'
            : 'clamp(290px,26%,340px) 1fr',
      }}
    >
      {/* LEFT — CTA Panel */}
      <div
        style={{
          position: 'relative',
          zIndex: 5,
          order: bp.isMobile ? 2 : undefined,
          display: 'flex',
          flexDirection: 'column',
          padding: bp.isMobile ? '18px 18px 24px' : '28px 24px',
          gap: bp.isMobile ? '10px' : '14px',
          background: 'linear-gradient(180deg, #0C0918 0%, #07050F 100%)',
          borderRight: '1px solid rgba(139,92,255,0.14)',
          overflow: 'hidden',
        }}
      >
        {/* Subtle ambient glow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: `radial-gradient(ellipse at 50% 0%, ${C.neonPurple}18 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* Logo / Branding */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${C.primaryPurple}, ${C.neonPurple})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              boxShadow: `0 0 14px ${C.neonPurple}66`,
            }}
          >
            ⚡
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Exo 2',sans-serif",
                fontWeight: 900,
                fontSize: '15px',
                color: C.white,
                letterSpacing: '0.08em',
                lineHeight: 1,
              }}
            >
              CHARGEN
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '8px',
                color: C.secText,
                letterSpacing: '0.12em',
                marginTop: '1px',
              }}
            >
              ETKİNLİK SİSTEMİ
            </div>
          </div>
        </div>

        {/* Stat card */}
        <GlassPanel style={{ padding: '10px 14px' }} glow={C.cyan}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: bp.isMobile ? '22px' : '28px',
                fontWeight: 700,
                color: C.cyan,
                textShadow: `0 0 12px ${C.cyan}`,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {entries.length}
            </div>
            <div
              style={{
                fontSize: '9px',
                fontWeight: 700,
                color: C.secText,
                letterSpacing: '0.1em',
                lineHeight: 1.4,
              }}
            >
              BU ETKİNLİKTE
              <br />
              KARAKTER OLUŞTURULDU
            </div>
          </div>
        </GlassPanel>

        {/* Title */}
        <div style={{ zIndex: 1 }}>
          <NeonText
            size={bp.isMobile ? 28 : bp.isTablet ? 36 : 42}
            weight={900}
            color={C.neonPurple}
            style={{
              letterSpacing: '0.06em',
              lineHeight: 1.05,
              animation: 'neon-flicker 10s ease-in-out infinite',
              display: 'block',
            }}
          >
            KARAKTERİNİ
            <br />
            OLUŞTUR
          </NeonText>
          <p
            style={{
              color: C.secText,
              fontSize: bp.isMobile ? '12px' : '13px',
              marginTop: '8px',
              lineHeight: 1.6,
              margin: '8px 0 0',
            }}
          >
            Fotoğrafını çek.
            <br />
            Arcade karakterine dönüş.
          </p>
        </div>

        {/* CTA */}
        <GlowBtn
          size={bp.isMobile ? 'lg' : 'xl'}
          fullWidth
          onClick={() => onNavigate(2)}
          style={{
            fontSize: bp.isMobile ? '17px' : '20px',
            padding: bp.isMobile ? '16px 0' : '20px 0',
            borderRadius: '14px',
            boxShadow: `0 0 28px rgba(139,92,255,0.65), 0 0 56px rgba(139,92,255,0.25)`,
            animation: 'glow-pulse 2.2s ease-in-out infinite',
            zIndex: 1,
          }}
        >
          BAŞLAT
        </GlowBtn>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            color: `${C.secText}88`,
            fontSize: '12px',
            zIndex: 1,
          }}
        >
          <span style={{ animation: 'float-slow 1.6s ease-in-out infinite', fontSize: '10px' }}>
            ▼
          </span>
          Başlamak için dokun
          <span
            style={{ animation: 'float-slow 1.6s 0.3s ease-in-out infinite', fontSize: '10px' }}
          >
            ▼
          </span>
        </div>

        {/* Last generated card */}
        {latestEntry && (
          <GlassPanel
            style={{
              padding: '10px 12px',
              border: `1px solid ${latestEntry.color}33`,
              marginTop: 'auto',
              zIndex: 1,
            }}
            glow={latestEntry.color}
          >
            <div
              style={{
                fontSize: '8px',
                fontWeight: 700,
                color: C.secText,
                letterSpacing: '0.14em',
                marginBottom: '8px',
                fontFamily: "'JetBrains Mono',monospace",
              }}
            >
              SON OLUŞTURULAN
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div
                style={{
                  animation: 'idle-breathe 2.5s ease-in-out infinite',
                  flexShrink: 0,
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CharacterFigure
                  outfitId={latestEntry.outfitId}
                  color={latestEntry.color}
                  headUrl={latestEntry.headUrl}
                  head={latestEntry.head}
                  size={30}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: '13px',
                    color: latestEntry.color,
                    textShadow: `0 0 8px ${latestEntry.color}`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: '0.04em',
                  }}
                >
                  {latestEntry.playerName.split(' ')[0]}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '9px',
                    color: C.secText,
                    marginTop: '2px',
                  }}
                >
                  PLAYER {latestEntry.playerNum}
                </div>
                <div style={{ marginTop: '4px' }}>
                  <StatusBadge variant="success">KARAKTER HAZIR</StatusBadge>
                </div>
              </div>
            </div>
          </GlassPanel>
        )}
      </div>

      {/* RIGHT — Character Collection */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          order: bp.isMobile ? 1 : undefined,
          minHeight: bp.isMobile ? '260px' : undefined,
        }}
      >
        {/* Arcade grid background */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            backgroundImage: `linear-gradient(rgba(139,92,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,255,0.06) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
        {/* Ambient particles layer */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            overflow: 'hidden',
          }}
        >
          <Particles count={bp.isMobile ? 8 : 16} color={C.neonPurple} />
        </div>
        {/* Section header */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px 6px',
            background: 'linear-gradient(180deg, rgba(5,3,12,0.92) 0%, transparent 100%)',
          }}
        >
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 800,
              fontSize: '11px',
              color: C.neonPurple,
              letterSpacing: '0.12em',
              textShadow: `0 0 8px ${C.neonPurple}`,
            }}
          >
            ⚡ KARAKTER KOLEKSİYONU
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {(['IDLE BOUNCE', 'WAVE', 'CELEBRATE'] as const).map((label) => {
              const col =
                label === 'WAVE' ? C.cyan : label === 'CELEBRATE' ? C.magenta : C.neonPurple
              return (
                <div
                  key={label}
                  style={{
                    background: `${col}18`,
                    border: `1px solid ${col}44`,
                    borderRadius: '4px',
                    padding: '2px 7px',
                    fontSize: '7px',
                    fontWeight: 800,
                    color: col,
                    fontFamily: "'JetBrains Mono',monospace",
                    letterSpacing: '0.08em',
                  }}
                >
                  {label}
                </div>
              )
            })}
          </div>
        </div>
        <div style={{ position: 'absolute', inset: 0, zIndex: 2, paddingTop: '30px' }}>
          <CollectionGrid cols={gridCols} />
        </div>
      </div>

      {/* Spotlight overlay — sits above everything, inset 0 */}
      {spotlightEntry && (
        <SpotlightOverlay
          entry={spotlightEntry}
          onDone={() => setDismissedSpotlight(spotlightEntry.uid)}
        />
      )}
    </div>
  )
}

// ─── Football Character SVG ───────────────────────────────────────────────────
// Kafa sprite'ı kontratı (HEAD_STANDARD_V1, ADR-009): kare tuval, pivot (0.5, 0.12) — yani
// görüntünün alt kenarından %12 yukarısı boyun bağlantı noktasıdır. Gövdenin boyun üstü
// (60, 44) olduğu için görüntü oraya bu pivottan oturtulur; Unity de aynı noktayı kullanır.
const HEAD_SPRITE_WIDTH = 56
const HEAD_PIVOT_Y = 0.12
const HEAD_X = 60 - HEAD_SPRITE_WIDTH / 2
const HEAD_Y = 44 - HEAD_SPRITE_WIDTH * (1 - HEAD_PIVOT_Y)

function FootballCharacterSVG({
  color = '#8B5CFF',
  size = 260,
  headReady = false,
  headUrl = null,
}: {
  color?: string
  size?: number
  headReady?: boolean
  headUrl?: string | null
}) {
  const skin = '#F5CBA7'
  return (
    <svg
      width={size}
      height={size * 1.4}
      viewBox="0 0 120 168"
      style={{ overflow: 'visible', filter: `drop-shadow(0 0 8px ${color}66)` }}
    >
      {headUrl ? (
        /* Gerçek kafa — worker'ın normalize ettiği sprite; kiosk üzerinde hiçbir düzeltme yapmaz */
        <image
          href={headUrl}
          x={HEAD_X}
          y={HEAD_Y}
          width={HEAD_SPRITE_WIDTH}
          height={HEAD_SPRITE_WIDTH}
          preserveAspectRatio="xMidYMid meet"
          style={{ filter: `drop-shadow(0 0 8px ${color}88)` }}
        />
      ) : headReady ? (
        /* Ready head — personalized face placeholder */
        <g>
          <circle
            cx="60"
            cy="22"
            r="19"
            fill={`${color}33`}
            stroke={color}
            strokeWidth="1.5"
            style={{ filter: `drop-shadow(0 0 10px ${color})` }}
          />
          {/* Face fill */}
          <circle cx="60" cy="22" r="15" fill={skin} opacity="0.85" />
          {/* Eyes */}
          <circle cx="54" cy="19" r="2.2" fill="#2D1B4E" />
          <circle cx="66" cy="19" r="2.2" fill="#2D1B4E" />
          {/* Eye shine */}
          <circle cx="55" cy="18" r="0.8" fill="white" opacity="0.8" />
          <circle cx="67" cy="18" r="0.8" fill="white" opacity="0.8" />
          {/* Smile */}
          <path
            d="M54 26 Q60 31 66 26"
            stroke="#2D1B4E"
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
          />
          {/* Eyebrows */}
          <path
            d="M51.5 16.5 Q54 15 56.5 16"
            stroke="#5D3A1A"
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
            opacity="0.7"
          />
          <path
            d="M63.5 16 Q66 15 68.5 16.5"
            stroke="#5D3A1A"
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
            opacity="0.7"
          />
          {/* Hair hint */}
          <path d="M43 19 Q43 8 60 7 Q77 8 77 19" fill={`${color}44`} stroke="none" />
          {/* Accent ring */}
          <circle
            cx="60"
            cy="22"
            r="19"
            fill="none"
            stroke={color}
            strokeWidth="1"
            style={{ filter: `drop-shadow(0 0 6px ${color}88)` }}
          />
        </g>
      ) : (
        /* Processing head — AI placeholder */
        <g>
          <circle
            cx="60"
            cy="22"
            r="19"
            fill={`${color}22`}
            stroke={color}
            strokeWidth="2"
            strokeDasharray="5 3"
            style={{ filter: `drop-shadow(0 0 10px ${color})` }}
          />
          <text
            x="60"
            y="18"
            textAnchor="middle"
            fill={`${color}cc`}
            fontSize="7"
            fontFamily="'JetBrains Mono',monospace"
          >
            AI
          </text>
          <text
            x="60"
            y="28"
            textAnchor="middle"
            fill={`${color}88`}
            fontSize="5.5"
            fontFamily="'JetBrains Mono',monospace"
          >
            KAFA
          </text>
        </g>
      )}
      {/* Neck */}
      <rect x="56" y="40" width="8" height="9" rx="2" fill={skin} />
      {/* Jersey */}
      <path
        d="M32 50 Q28 47 26 58 L20 86 L100 86 L94 58 Q92 47 88 50 L72 44 Q66 49 60 49 Q54 49 48 44 Z"
        fill={color}
        style={{ filter: `drop-shadow(0 0 4px ${color}44)` }}
      />
      <rect x="56" y="49" width="8" height="37" rx="2" fill="rgba(255,255,255,0.15)" />
      <text
        x="60"
        y="79"
        textAnchor="middle"
        fill="rgba(255,255,255,0.9)"
        fontSize="13"
        fontWeight="bold"
        fontFamily="'Exo 2',sans-serif"
      >
        10
      </text>
      {/* Left arm */}
      <line x1="32" y1="52" x2="14" y2="72" stroke={color} strokeWidth="13" strokeLinecap="round" />
      <circle cx="13" cy="73" r="6" fill={skin} />
      {/* Right arm */}
      <line
        x1="88"
        y1="52"
        x2="106"
        y2="72"
        stroke={color}
        strokeWidth="13"
        strokeLinecap="round"
      />
      <circle cx="107" cy="73" r="6" fill={skin} />
      {/* Belt */}
      <rect x="20" y="82" width="80" height="6" rx="3" fill={`${color}88`} />
      {/* Shorts */}
      <path d="M20 87 L28 126 L54 126 L60 106 L66 126 L92 126 L100 87 Z" fill="#1a1535" />
      <rect x="20" y="87" width="80" height="5" fill={`${color}33`} />
      {/* Socks */}
      <rect x="28" y="126" width="22" height="24" rx="3" fill="#0d0a20" />
      <rect x="28" y="126" width="22" height="5" fill={`${color}66`} />
      <rect x="70" y="126" width="22" height="24" rx="3" fill="#0d0a20" />
      <rect x="70" y="126" width="22" height="5" fill={`${color}66`} />
      {/* Boots */}
      <rect x="25" y="144" width="26" height="11" rx="4" fill={color} />
      <ellipse
        cx="38"
        cy="153"
        rx="16"
        ry="6"
        fill={color}
        style={{ filter: `drop-shadow(0 0 4px ${color})` }}
      />
      <rect x="69" y="144" width="26" height="11" rx="4" fill={color} />
      <ellipse
        cx="82"
        cy="153"
        rx="16"
        ry="6"
        fill={color}
        style={{ filter: `drop-shadow(0 0 4px ${color})` }}
      />
    </svg>
  )
}

// ─── Screen 02 — How It Works ─────────────────────────────────────────────────
function Screen02({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()

  const steps = [
    {
      num: '01',
      icon: (color: string) => (
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
          <circle cx="22" cy="19" r="9" stroke={color} strokeWidth="2.2" />
          <circle cx="22" cy="19" r="4" fill={color} opacity="0.7" />
          <rect x="7" y="13" width="30" height="22" rx="5" stroke={color} strokeWidth="2.2" />
          <path
            d="M16 13v-2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="35" cy="16" r="2" fill={color} opacity="0.6" />
        </svg>
      ),
      title: 'FOTOĞRAFINI ÇEK',
      desc: 'Kameraya bak. Yüzünün net görünmesi yeterli.',
      accent: C.cyan,
    },
    {
      num: '02',
      icon: (color: string) => (
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
          <circle cx="22" cy="18" r="8" stroke={color} strokeWidth="2.2" />
          <path
            d="M22 10 l1.5 3 3.5 0.5-2.5 2.5 0.6 3.5L22 18l-3.1 1.5 0.6-3.5L17 13.5l3.5-0.5z"
            fill={color}
            opacity="0.8"
          />
          <path
            d="M14 30c0-4.4 3.6-8 8-8s8 3.6 8 8"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
          {[0, 1, 2].map((i) => (
            <circle
              key={i}
              cx={22 + (i - 1) * 6}
              cy={7 - (i % 2) * 3}
              r="1.5"
              fill={color}
              opacity={0.5 - i * 0.1}
              style={{ animation: `glow-pulse 1.2s ${i * 0.3}s ease-in-out infinite` }}
            />
          ))}
        </svg>
      ),
      title: 'KARAKTERİN OLUŞSUN',
      desc: 'Yapay zekâ seni arcade oyun karakterine dönüştürsün.',
      accent: C.neonPurple,
    },
    {
      num: '03',
      icon: (color: string) => (
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
          <rect x="15" y="6" width="14" height="24" rx="3" stroke={color} strokeWidth="2.2" />
          <rect x="18" y="27" width="8" height="1.5" rx="0.75" fill={color} opacity="0.6" />
          <rect x="20" y="9" width="4" height="0.5" rx="0.25" fill={color} opacity="0.4" />
          <rect x="20" y="13" width="7" height="7" rx="1" stroke={color} strokeWidth="1.4" />
          <path d="M22 13v7M20 16h7" stroke={color} strokeWidth="1" opacity="0.5" />
          <path
            d="M33 19c0 5.5-4.5 10-11 10"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeDasharray="2.5 2"
          />
          <circle cx="22" cy="30" r="1.2" fill={color} />
        </svg>
      ),
      title: 'TELEFONUNDAN TAKİP ET',
      desc: 'QR kodunu okut, karakterin hazır olduğunda telefonundan gör.',
      accent: '#00E676',
    },
  ]

  const isHoriz = !bp.isMobile

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(ellipse at 50% 15%, #2A1660 0%, #140D26 50%, ${C.deepBlack} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stars count={60} />

      {/* Subtle grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.04,
          backgroundImage: `linear-gradient(rgba(139,92,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,255,1) 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: bp.isMobile ? '20px' : '28px',
          padding: bp.isMobile ? '20px 16px' : '0 48px',
          width: '100%',
          maxWidth: '1100px',
        }}
      >
        {/* Title */}
        <div style={{ textAlign: 'center', animation: 'slide-up 0.4s ease both' }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '10px',
              fontWeight: 700,
              color: C.neonPurple,
              letterSpacing: '0.2em',
              marginBottom: '10px',
              opacity: 0.8,
            }}
          >
            ⚡ CHARGEN
          </div>
          <NeonText
            size={bp.isMobile ? 26 : bp.isTablet ? 34 : 44}
            weight={900}
            color={C.white}
            style={{ letterSpacing: '0.06em', lineHeight: 1, textShadow: 'none' }}
          >
            3 ADIMDA
            <br />
            <span style={{ color: C.white }}>KARAKTERİN HAZIR</span>
          </NeonText>
        </div>

        {/* Steps */}
        <div
          style={{
            display: 'flex',
            flexDirection: isHoriz ? 'row' : 'column',
            gap: isHoriz ? '0' : '10px',
            alignItems: isHoriz ? 'stretch' : 'center',
            width: '100%',
          }}
        >
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              {/* Card */}
              <div
                style={{
                  flex: isHoriz ? '1 1 0' : undefined,
                  width: isHoriz ? undefined : '100%',
                  maxWidth: isHoriz ? undefined : '440px',
                  animation: `slide-up ${0.2 + i * 0.12}s ease both`,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    padding: bp.isMobile ? '18px 20px' : '28px 28px',
                    borderRadius: '16px',
                    background: `linear-gradient(160deg, ${step.accent}14 0%, rgba(12,8,24,0.9) 60%, ${step.accent}08 100%)`,
                    border: `1px solid ${step.accent}33`,
                    boxShadow: `0 0 28px ${step.accent}14, inset 0 0 20px ${step.accent}06`,
                    display: 'flex',
                    flexDirection: isHoriz ? 'column' : 'row',
                    alignItems: isHoriz ? 'flex-start' : 'center',
                    gap: isHoriz ? '16px' : '18px',
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  {/* Step number + icon */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isHoriz ? 'column' : 'row',
                      alignItems: isHoriz ? 'flex-start' : 'center',
                      gap: '10px',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontWeight: 900,
                        fontSize: bp.isMobile ? '11px' : '13px',
                        color: step.accent,
                        letterSpacing: '0.12em',
                        textShadow: `0 0 10px ${step.accent}88`,
                      }}
                    >
                      {step.num}
                    </div>
                    <div
                      style={{
                        width: isHoriz ? '64px' : '52px',
                        height: isHoriz ? '64px' : '52px',
                        borderRadius: isHoriz ? '18px' : '14px',
                        flexShrink: 0,
                        background: `radial-gradient(circle, ${step.accent}22 0%, ${step.accent}08 100%)`,
                        border: `1.5px solid ${step.accent}44`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 0 18px ${step.accent}33`,
                      }}
                    >
                      {step.icon(step.accent)}
                    </div>
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontFamily: "'Exo 2',sans-serif",
                        fontWeight: 900,
                        fontSize: bp.isMobile ? '14px' : '16px',
                        color: C.white,
                        letterSpacing: '0.06em',
                        lineHeight: 1.1,
                        marginBottom: '6px',
                      }}
                    >
                      {step.title}
                    </div>
                    <div
                      style={{
                        fontSize: bp.isMobile ? '12px' : '13px',
                        color: C.secText,
                        lineHeight: 1.6,
                      }}
                    >
                      {step.desc}
                    </div>
                  </div>
                </div>
              </div>

              {/* Connector arrow */}
              {isHoriz && i < steps.length - 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    flexShrink: 0,
                    alignSelf: 'center',
                  }}
                >
                  <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                    <path
                      d="M2 10 H26 M20 4 L28 10 L20 16"
                      stroke={`${C.neonPurple}66`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* CTAs */}
        <div
          style={{
            display: 'flex',
            gap: '14px',
            flexDirection: bp.isMobile ? 'column' : 'row',
            alignItems: 'center',
            width: '100%',
            maxWidth: bp.isMobile ? '380px' : 'none',
            justifyContent: 'center',
            animation: 'slide-up 0.5s 0.35s ease both',
          }}
        >
          <GlowBtn
            size="xl"
            onClick={() => onNavigate(3)}
            style={{
              fontSize: bp.isMobile ? '18px' : '20px',
              minWidth: bp.isMobile ? '100%' : '280px',
              boxShadow: `0 0 28px rgba(139,92,255,0.6), 0 0 56px rgba(139,92,255,0.22)`,
            }}
          >
            HAZIRIM →
          </GlowBtn>
          <GlowBtn
            size="lg"
            variant="secondary"
            onClick={() => onNavigate(1)}
            style={{
              fontSize: bp.isMobile ? '15px' : '16px',
              minWidth: bp.isMobile ? '100%' : '140px',
            }}
          >
            ← GERİ
          </GlowBtn>
        </div>
      </div>
    </div>
  )
}

// ─── Screen 03 — Portrait Capture ────────────────────────────────────────────
/**
 * Kılavuz ovalinin geometrisi — **hem çizilen SVG hem ölçülen bölge** bunu kullanır. Ayrı
 * kalırlarsa misafirin gördüğü kadraj ile kontrol ettiğimiz kadraj ayrışır (24 Ağu bulgusu:
 * ovalin dışına çıkınca "kadraj uygun" yeşil kalıyordu).
 */
const GUIDE = { viewW: 600, viewH: 720, cx: 300, cy: 330, rx: 155, ry: 210, pad: 1.1 }

/** Ovalin sınır kutusu, videonun görünen alanına göre 0-1 normalize. */
function guideRegion(video: HTMLVideoElement): Region | null {
  const box = video.getBoundingClientRect()
  if (box.width === 0 || box.height === 0) return null
  // SVG overlay `preserveAspectRatio="xMidYMid meet"` ile yerleşiyor: aynı hesap
  const fit = Math.min(box.width / GUIDE.viewW, box.height / GUIDE.viewH)
  const originX = (box.width - GUIDE.viewW * fit) / 2
  const originY = (box.height - GUIDE.viewH * fit) / 2
  const halfW = GUIDE.rx * fit * GUIDE.pad
  const halfH = GUIDE.ry * fit * GUIDE.pad
  const centerX = originX + GUIDE.cx * fit
  const centerY = originY + GUIDE.cy * fit
  return {
    x0: Math.max(0, (centerX - halfW) / box.width),
    y0: Math.max(0, (centerY - halfH) / box.height),
    x1: Math.min(1, (centerX + halfW) / box.width),
    y1: Math.min(1, (centerY + halfH) / box.height),
  }
}

/**
 * Göz açıklığı **kişiye ve kamera açısına** göre değişiyor (ölçülen: aynı kişide açık göz
 * 0.18-0.26; sabit bir eşik kimini yanlış reddeder, kiminde kapalı gözü kaçırır). Bu yüzden
 * misafirin kendi tepe değeri temel alınır: o oturumdaki en açık göze göre belirgin düşüş =
 * kapalı. Sunucunun mutlak tabanı (frame_quality.py) yine geçerlidir — ikisi birlikte.
 */
const EYE_BASELINE_RATIO = 0.72
const EYE_WINDOW = 10

function withEyeBaseline(result: FrameVerdict, history: { current: number[] }): FrameVerdict {
  const value = result.measurements?.eyeOpenness
  if (!result.faceFound || value === undefined) return result
  history.current = [...history.current, value].slice(-EYE_WINDOW)
  if (history.current.length < 5) return result
  // Tepe değer olarak **en yüksek üçün ortancası**: tek bir uç ölçüm (0.32 gibi) eşiği yukarı
  // çekip açık gözü kapalı saymasın. Ölçülen: açık 0.18-0.32, kapalı 0.14-0.16 (24 Ağu).
  const top = [...history.current].sort((a, b) => b - a).slice(0, 3)
  const baseline = top[Math.min(1, top.length - 1)]!
  const eyesOpen = result.eyesOpen && value >= baseline * EYE_BASELINE_RATIO
  return {
    ...result,
    eyesOpen,
    ok: result.ok && eyesOpen,
    hint: eyesOpen ? result.hint : 'Gözlerini aç',
  }
}

/** Son karelerin çoğunluğu: tek karelik gürültü kontrol listesini oynatmasın. */
function smooth(samples: FrameVerdict[]): FrameVerdict {
  const last = samples[samples.length - 1]!
  const majority = (pick: (v: FrameVerdict) => boolean) =>
    samples.filter(pick).length * 2 > samples.length
  const faceFound = majority((v) => v.faceFound)
  const eyesOpen = majority((v) => v.eyesOpen)
  const sharp = majority((v) => v.sharp)
  const framed = majority((v) => v.framed)
  return {
    faceFound,
    eyesOpen,
    sharp,
    framed,
    ok: faceFound && eyesOpen && sharp && framed,
    hint: faceFound && eyesOpen && sharp && framed ? null : last.hint,
  }
}

function Screen03({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { setCapture } = useKiosk()
  const { videoRef, status, quality, capture, snapshot, regionInSource } = useCamera(true)
  const [shooting, setShooting] = useState(false)
  const [verdict, setVerdict] = useState<FrameVerdict | null>(null)
  const [checkDown, setCheckDown] = useState(false)
  const recent = useRef<FrameVerdict[]>([])
  const openness = useRef<number[]>([])

  const live = status === 'live'

  // Kalite kontrolü sunucuda, üretimle **aynı dedektörle** yapılır (#31): kiosk'un onayladığı
  // kare worker'ın işleyebildiği karedir. Kare saklanmaz; ölçülür ve düşer.
  useEffect(() => {
    if (!live) return
    let stopped = false
    let failures = 0
    const ac = new AbortController()

    const tick = async () => {
      const video = videoRef.current
      if (!video) return
      const region = guideRegion(video)
      if (!region) return
      // Ovalin içi kesilip yollanır: misafirin gördüğü kadraj = ölçtüğümüz kadraj
      const frame = await snapshot(region)
      if (!frame || stopped) return
      try {
        const result = await checkFrame(frame, ac.signal)
        if (stopped) return
        // Yumuşatma: kamera gürültüsü tek karede eşiğin altına düşebiliyor (ölçülen netlik
        // 36-56 arasında oynuyor). Üç karenin ikisi diyorsa öyledir — liste titremez.
        recent.current = [...recent.current, withEyeBaseline(result, openness)].slice(-3)
        setVerdict(smooth(recent.current))
        failures = 0
        setCheckDown(false)
      } catch {
        if (stopped) return
        failures += 1
        // Kontrol erişilemiyorsa misafiri sonsuza kadar tutmayız: birkaç denemeden sonra
        // deklanşör açılır (kafa kontratı worker'da yine bekçilik eder), durum ekranda yazar.
        if (failures >= 5) setCheckDown(true)
      }
    }

    void tick()
    const timer = setInterval(() => void tick(), 700)
    return () => {
      stopped = true
      ac.abort()
      clearInterval(timer)
      recent.current = []
      openness.current = []
    }
  }, [live, snapshot, videoRef])

  const checks = [
    { label: 'Yüz algılandı', ok: verdict?.faceFound === true },
    { label: 'Gözler açık', ok: verdict?.eyesOpen === true },
    { label: 'Yüz net', ok: verdict?.sharp === true },
    { label: 'Kadraj uygun', ok: verdict?.framed === true },
  ]

  const faceDetected = verdict?.faceFound === true
  const allGood = live && (checkDown || verdict?.ok === true)
  const statusText = !live
    ? 'KAMERA BEKLENİYOR'
    : checkDown
      ? 'KONTROL YAPILAMIYOR'
      : !quality.brightEnough && !faceDetected
        ? 'IŞIK YETERSİZ'
        : (verdict?.hint?.toLocaleUpperCase('tr-TR') ?? 'HAZIR')

  const shoot = async () => {
    if (!allGood || shooting) return
    setShooting(true)
    try {
      // Deklanşör anı: canlı verdict en fazla ~0.7 sn eskidir; tam o anda göz kırpılırsa
      // kaçırırdık. Kare alınır **ve aynı kare bir kez daha ölçülür** — üretime giden fotoğraf
      // budur (paralı çağrıyı boşa harcamamak için son kapı).
      const video = videoRef.current
      const region = video ? guideRegion(video) : null
      // Kılavuzun ham karedeki yeri **kareyle aynı anda** okunur: video boyu ya da yerleşim
      // sonradan değişirse sunucu fotoğrafın başka bir yerini keserdi (#98).
      const source = region ? regionInSource(region) : null
      const [blob, frame] = await Promise.all([capture(), region ? snapshot(region) : null])
      if (!blob) return
      if (frame && !checkDown) {
        try {
          const final = await checkFrame(frame)
          if (!final.faceFound || !final.eyesOpen) {
            setVerdict(final)
            recent.current = [final]
            return
          }
        } catch {
          // kontrol erişilemedi: kare zaten canlı denetimden geçmişti, akış durmaz
        }
      }
      // Bölge fotoğrafla birlikte gider: fal karenin tamamını görürse ovalin **dışındaki**
      // yüzü seçebiliyor — 27 Ağu gecesi canlıda seçti.
      setCapture({ blob, url: URL.createObjectURL(blob), region: source })
      onNavigate(4)
    } finally {
      setShooting(false)
    }
  }

  // Oval stroke color transitions through states
  const ovalColor = allGood ? '#00E676' : faceDetected ? C.neonPurple : `${C.secText}55`
  const ovalDash = allGood ? 'none' : '10 5'

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#050310',
        display: bp.isMobile ? 'flex' : 'grid',
        flexDirection: bp.isMobile ? 'column' : undefined,
        gridTemplateColumns: bp.isMobile ? undefined : 'clamp(270px,25%,310px) 1fr',
      }}
    >
      {/* ── LEFT — Info panel ─────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: bp.isMobile ? '16px 18px' : '32px 24px',
          gap: '18px',
          background: 'linear-gradient(180deg, #0C0818 0%, #070510 100%)',
          borderRight: '1px solid rgba(139,92,255,0.12)',
          order: bp.isMobile ? 2 : undefined,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Top glow accent */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '160px',
            background: `radial-gradient(ellipse at 50% 0%, ${C.neonPurple}18 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* Title */}
        <div style={{ zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9px',
              fontWeight: 700,
              color: C.neonPurple,
              letterSpacing: '0.18em',
              marginBottom: '8px',
              opacity: 0.8,
            }}
          >
            PORTRE ÇEKIMI
          </div>
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: bp.isMobile ? '22px' : '28px',
              color: C.white,
              lineHeight: 1.1,
              letterSpacing: '0.04em',
            }}
          >
            KAMERAYA
            <br />
            BAK
          </div>
          <div style={{ fontSize: '13px', color: C.secText, marginTop: '8px', lineHeight: 1.6 }}>
            Yüzünü çerçevenin içine getir.
          </div>
        </div>

        {/* Checklist */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', zIndex: 1 }}>
          {checks.map((c, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 14px',
                borderRadius: '10px',
                background: c.ok ? 'rgba(0,230,118,0.07)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${c.ok ? 'rgba(0,230,118,0.25)' : 'rgba(255,255,255,0.07)'}`,
                transition: 'all 0.4s ease',
              }}
            >
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  border: `2px solid ${c.ok ? '#00E676' : 'rgba(255,255,255,0.18)'}`,
                  background: c.ok ? 'rgba(0,230,118,0.18)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.4s ease',
                  boxShadow: c.ok ? '0 0 8px rgba(0,230,118,0.4)' : 'none',
                }}
              >
                {c.ok && (
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                    <path
                      d="M1 4.5L4 7.5L10 1"
                      stroke="#00E676"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: c.ok ? 700 : 400,
                  color: c.ok ? C.white : C.secText,
                  transition: 'all 0.3s ease',
                }}
              >
                {c.label}
              </span>
              {c.ok && i === checks.findIndex((x) => !x.ok) - 1 && (
                <div
                  style={{
                    marginLeft: 'auto',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#00E676',
                    boxShadow: '0 0 6px #00E676',
                    animation: 'glow-pulse 1s ease-in-out infinite',
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Status readout */}
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '10px',
            zIndex: 1,
            background: allGood
              ? 'rgba(0,230,118,0.1)'
              : faceDetected
                ? 'rgba(139,92,255,0.1)'
                : 'rgba(255,255,255,0.04)',
            border: `1px solid ${allGood ? 'rgba(0,230,118,0.3)' : faceDetected ? 'rgba(139,92,255,0.25)' : 'rgba(255,255,255,0.08)'}`,
            transition: 'all 0.4s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              flexShrink: 0,
              background: allGood ? '#00E676' : faceDetected ? C.neonPurple : C.secText,
              boxShadow: allGood
                ? '0 0 8px #00E676'
                : faceDetected
                  ? `0 0 8px ${C.neonPurple}`
                  : 'none',
              animation: faceDetected && !allGood ? 'glow-pulse 1.2s ease-in-out infinite' : 'none',
              transition: 'all 0.4s ease',
            }}
          />
          <span
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 700,
              fontSize: '12px',
              letterSpacing: '0.06em',
              color: allGood ? '#00E676' : faceDetected ? C.neonPurple : C.secText,
              transition: 'color 0.4s ease',
            }}
          >
            {statusText}
          </span>
        </div>

        {/* CTAs */}
        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            zIndex: 1,
          }}
        >
          <GlowBtn
            size="xl"
            fullWidth
            onClick={() => {
              void shoot()
            }}
            style={{
              fontSize: '17px',
              opacity: allGood ? 1 : 0.35,
              pointerEvents: allGood && !shooting ? 'auto' : 'none',
              transition: 'opacity 0.4s ease',
              boxShadow: allGood ? `0 0 28px rgba(139,92,255,0.55)` : 'none',
            }}
          >
            {shooting ? 'ÇEKİLİYOR…' : 'FOTOĞRAF ÇEK 📸'}
          </GlowBtn>
          <GlowBtn size="lg" variant="secondary" fullWidth onClick={() => onNavigate(2)}>
            ← GERİ
          </GlowBtn>
        </div>
      </div>

      {/* ── RIGHT — Live camera feed ───────────────────── */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: '#030208',
          order: bp.isMobile ? 1 : undefined,
          minHeight: bp.isMobile ? '55vw' : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Subtle scanline texture */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(139,92,255,0.018) 3px, rgba(139,92,255,0.018) 4px)',
          }}
        />

        {/* Gerçek kamera akışı — önizleme aynalı, kayıt aynasız (useCamera) */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'scaleX(-1)',
            background: 'linear-gradient(160deg, #11092A 0%, #0A0618 50%, #080410 100%)',
          }}
        />
        {!live && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              background: 'linear-gradient(160deg, #11092A 0%, #0A0618 50%, #080410 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            <div style={{ fontSize: '64px', opacity: 0.25 }}>📷</div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '12px',
                color: C.secText,
                letterSpacing: '0.14em',
              }}
            >
              {status === 'denied'
                ? 'KAMERA İZNİ YOK'
                : status === 'unavailable'
                  ? 'KAMERA BULUNAMADI'
                  : 'KAMERA AÇILIYOR…'}
            </div>
          </div>
        )}

        {/* Face guidance oval — SVG overlay fills the whole camera area */}
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 2,
          }}
          viewBox="0 0 600 720"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <mask id="ovalMask">
              <rect width="600" height="720" fill="white" />
              {/* Oval cutout — tall enough for hair + face + slight shoulder */}
              <ellipse cx="300" cy="330" rx="155" ry="210" fill="black" />
            </mask>
          </defs>

          {/* Dark vignette outside oval */}
          <rect width="600" height="720" fill="rgba(0,0,0,0.62)" mask="url(#ovalMask)" />

          {/* Oval border */}
          <ellipse
            cx="300"
            cy="330"
            rx="155"
            ry="210"
            fill="none"
            stroke={ovalColor}
            strokeWidth="2.5"
            strokeDasharray={ovalDash}
            style={{
              transition: 'stroke 0.5s ease, filter 0.5s ease',
              filter: `drop-shadow(0 0 8px ${ovalColor}) drop-shadow(0 0 18px ${ovalColor}66)`,
            }}
          />

          {/* Hair guide tick — top */}
          <line
            x1="270"
            y1="112"
            x2="330"
            y2="112"
            stroke={`${ovalColor}88`}
            strokeWidth="2"
            strokeLinecap="round"
            style={{ transition: 'stroke 0.5s ease' }}
          />
          <text
            x="300"
            y="104"
            textAnchor="middle"
            fill={`${ovalColor}77`}
            fontSize="10"
            fontFamily="'JetBrains Mono',monospace"
            letterSpacing="2"
            style={{ transition: 'fill 0.5s ease' }}
          >
            SAÇ
          </text>

          {/* Shoulder guide tick — bottom */}
          <line
            x1="170"
            y1="548"
            x2="430"
            y2="548"
            stroke={`${ovalColor}66`}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="4 4"
            style={{ transition: 'stroke 0.5s ease' }}
          />
          <text
            x="300"
            y="564"
            textAnchor="middle"
            fill={`${ovalColor}66`}
            fontSize="10"
            fontFamily="'JetBrains Mono',monospace"
            letterSpacing="2"
            style={{ transition: 'fill 0.5s ease' }}
          >
            OMUZ
          </text>

          {/* Corner brackets — camera frame */}
          {/* Top-left */}
          <path
            d="M 40 60 L 40 40 L 60 40"
            stroke={`${C.cyan}88`}
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />
          {/* Top-right */}
          <path
            d="M 540 60 L 540 40 L 520 40"
            stroke={`${C.cyan}88`}
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />
          {/* Bottom-left */}
          <path
            d="M 40 660 L 40 680 L 60 680"
            stroke={`${C.cyan}88`}
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />
          {/* Bottom-right */}
          <path
            d="M 540 660 L 540 680 L 520 680"
            stroke={`${C.cyan}88`}
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />
        </svg>

        {/* Scan line — only when face detected */}
        {faceDetected && (
          <div
            style={{
              position: 'absolute',
              left: '18%',
              right: '18%',
              height: '1.5px',
              zIndex: 3,
              background: `linear-gradient(90deg, transparent, ${allGood ? '#00E676' : C.cyan} 50%, transparent)`,
              boxShadow: `0 0 10px ${allGood ? '#00E676' : C.cyan}`,
              animation: 'scan-line 2.4s ease-in-out infinite',
              pointerEvents: 'none',
              transition: 'background 0.5s ease',
            }}
          />
        )}

        {/* Top-center status pill */}
        <div
          style={{
            position: 'absolute',
            top: '18px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 4,
            whiteSpace: 'nowrap',
            padding: '7px 20px',
            borderRadius: '999px',
            background: allGood
              ? 'rgba(0,230,118,0.18)'
              : faceDetected
                ? 'rgba(139,92,255,0.2)'
                : 'rgba(10,6,22,0.8)',
            border: `1px solid ${allGood ? '#00E67666' : faceDetected ? `${C.neonPurple}66` : 'rgba(255,255,255,0.1)'}`,
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.5s ease',
            boxShadow: allGood
              ? '0 0 20px rgba(0,230,118,0.25)'
              : faceDetected
                ? `0 0 16px ${C.neonPurple}33`
                : 'none',
          }}
        >
          <div
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: allGood ? '#00E676' : faceDetected ? C.neonPurple : `${C.secText}66`,
              boxShadow: allGood
                ? '0 0 6px #00E676'
                : faceDetected
                  ? `0 0 6px ${C.neonPurple}`
                  : 'none',
              animation: faceDetected ? 'glow-pulse 1.2s ease-in-out infinite' : 'none',
              transition: 'all 0.5s ease',
            }}
          />
          <span
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 800,
              fontSize: '12px',
              letterSpacing: '0.1em',
              color: allGood ? '#00E676' : faceDetected ? C.white : `${C.secText}bb`,
              transition: 'color 0.5s ease',
            }}
          >
            {allGood ? `● ${statusText}` : `◉ ${statusText}`}
          </span>
        </div>

        {/* Ambient particle layer */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          <Particles count={bp.isMobile ? 6 : 10} color={allGood ? '#00E67655' : C.neonPurple} />
        </div>
      </div>
    </div>
  )
}

// ─── Screen 04 — Photo Review & Player Name ──────────────────────────────────
function Screen04({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { capture, displayName, setDisplayName } = useKiosk()
  const [consent, setConsent] = useState(false)
  const [focused, setFocused] = useState(false)
  const [showAydinlatma, setShowAydinlatma] = useState(false)

  // Foto yoksa (yenileme/derin bağlantı) çekim ekranına dön — boş POST'a yol yok
  useEffect(() => {
    if (!capture) onNavigate(3)
  }, [capture, onNavigate])

  const playerName = displayName
  const setPlayerName = setDisplayName
  const canProceed = playerName.trim().length > 0 && consent

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#060410',
        display: bp.isMobile ? 'flex' : 'grid',
        flexDirection: bp.isMobile ? 'column' : undefined,
        gridTemplateColumns: bp.isMobile ? undefined : '55fr 45fr',
      }}
    >
      {/* ── LEFT — Portrait preview ───────────────────── */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: '#030208',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          order: bp.isMobile ? 1 : undefined,
          minHeight: bp.isMobile ? '42vw' : undefined,
        }}
      >
        {/* Camera-style scanlines */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(139,92,255,0.015) 3px, rgba(139,92,255,0.015) 4px)',
            zIndex: 1,
          }}
        />

        {/* Portrait frame — 3:4 aspect, centered */}
        <div
          style={{
            position: 'relative',
            height: bp.isMobile ? '100%' : 'calc(100% - 32px)',
            aspectRatio: '3/4',
            maxWidth: bp.isMobile ? '55%' : '72%',
            borderRadius: '8px',
            overflow: 'hidden',
            zIndex: 2,
          }}
        >
          {/* Çekilen kare — gönderilecek foto neyse ekranda o vardır */}
          {capture && (
            <img
              src={capture.url}
              alt="Çekilen portre"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                background: 'linear-gradient(160deg, #1A1040 0%, #0E0828 50%, #160D38 100%)',
              }}
            />
          )}

          {/* Corner brackets */}
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                zIndex: 3,
                top: i < 2 ? '10px' : 'auto',
                bottom: i >= 2 ? '10px' : 'auto',
                left: i % 2 === 0 ? '10px' : 'auto',
                right: i % 2 !== 0 ? '10px' : 'auto',
                width: '22px',
                height: '22px',
                borderTop: i < 2 ? `2px solid ${C.cyan}aa` : 'none',
                borderBottom: i >= 2 ? `2px solid ${C.cyan}aa` : 'none',
                borderLeft: i % 2 === 0 ? `2px solid ${C.cyan}aa` : 'none',
                borderRight: i % 2 !== 0 ? `2px solid ${C.cyan}aa` : 'none',
              }}
            />
          ))}
        </div>

        {/* Approved stamp — top right of photo column */}
        <div
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 3,
            background: 'rgba(0,230,118,0.15)',
            border: '1px solid rgba(0,230,118,0.35)',
            borderRadius: '8px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
            <path
              d="M1 5L4.5 8.5L11 1"
              stroke="#00E676"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontSize: '11px',
              fontWeight: 800,
              color: '#00E676',
              letterSpacing: '0.1em',
            }}
          >
            ÇEKİLDİ
          </span>
        </div>
      </div>

      {/* ── RIGHT — Name form ─────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: bp.isMobile ? '20px 18px 24px' : '36px 32px',
          gap: '20px',
          background: 'linear-gradient(180deg, #0C0818 0%, #080512 100%)',
          borderLeft: '1px solid rgba(139,92,255,0.12)',
          order: bp.isMobile ? 2 : undefined,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Top glow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '150px',
            background: `radial-gradient(ellipse at 50% 0%, ${C.neonPurple}14 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* Title */}
        <div style={{ zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9px',
              fontWeight: 700,
              color: C.neonPurple,
              letterSpacing: '0.18em',
              marginBottom: '8px',
              opacity: 0.75,
            }}
          >
            FOTOĞRAF ONAY
          </div>
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: bp.isMobile ? '20px' : '26px',
              color: C.white,
              lineHeight: 1.15,
              letterSpacing: '0.03em',
            }}
          >
            BU FOTOĞRAFI
            <br />
            KULLANALIM MI?
          </div>
        </div>

        {/* Name input */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', zIndex: 1 }}>
          <label
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9px',
              fontWeight: 700,
              color: C.secText,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            OYUNCU ADIN
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="Adını yaz"
              maxLength={24}
              autoComplete="off"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: 'rgba(12,6,26,0.85)',
                border: `1.5px solid ${focused ? C.neonPurple : playerName ? `${C.neonPurple}88` : 'rgba(139,92,255,0.2)'}`,
                borderRadius: '12px',
                padding: '14px 48px 14px 18px',
                color: C.white,
                fontSize: '16px',
                fontFamily: "'Exo 2',sans-serif",
                fontWeight: 700,
                outline: 'none',
                caretColor: C.neonPurple,
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                boxShadow: focused
                  ? `0 0 0 3px ${C.neonPurple}22, 0 0 20px ${C.neonPurple}22`
                  : playerName
                    ? `0 0 12px ${C.neonPurple}18`
                    : 'none',
              }}
            />
            {/* Trailing check icon when filled */}
            {playerName && (
              <div
                style={{
                  position: 'absolute',
                  right: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(0,230,118,0.15)',
                  border: '1.5px solid rgba(0,230,118,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                  <path
                    d="M1 4.5L4 7.5L10 1"
                    stroke="#00E676"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </div>
          <div style={{ fontSize: '11px', color: `${C.secText}88`, lineHeight: 1.5 }}>
            Bu isim karakter kartında görünecek.
          </div>
        </div>

        {/* Consent checkbox */}
        <div
          onClick={() => setConsent((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px 14px',
            borderRadius: '10px',
            cursor: 'pointer',
            background: consent ? 'rgba(139,92,255,0.08)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${consent ? `${C.neonPurple}44` : 'rgba(255,255,255,0.08)'}`,
            transition: 'all 0.2s ease',
            zIndex: 1,
            userSelect: 'none',
          }}
        >
          {/* Custom checkbox */}
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              flexShrink: 0,
              marginTop: '1px',
              border: `2px solid ${consent ? C.neonPurple : 'rgba(255,255,255,0.22)'}`,
              background: consent ? C.neonPurple : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: consent ? `0 0 8px ${C.neonPurple}66` : 'none',
            }}
          >
            {consent && (
              <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                <path
                  d="M1 4.5L4 7.5L10 1"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span
            style={{
              fontSize: '11px',
              color: consent ? C.secText : `${C.secText}77`,
              lineHeight: 1.55,
              transition: 'color 0.2s ease',
            }}
          >
            KVKK kapsamında fotoğrafımın bu etkinlik için karakter oluşturmak amacıyla işlenmesine
            açık rızam vardır.{' '}
            <span
              onClick={(e) => {
                // Metni okumak onayı tetiklemesin -- "okudum" ile "kabul ediyorum" ayrı
                // eylemler, biri diğerini otomatik işaretlememeli.
                e.stopPropagation()
                setShowAydinlatma((v) => !v)
              }}
              style={{
                color: C.neonPurple,
                textDecoration: 'underline',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              Aydınlatma metnini oku
            </span>
          </span>
        </div>

        {showAydinlatma && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '180px',
              overflowY: 'auto',
              padding: '14px 16px',
              borderRadius: '10px',
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              zIndex: 1,
            }}
          >
            {KVKK_AYDINLATMA_METNI.map((paragraph, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontSize: '10.5px',
                  lineHeight: 1.6,
                  color: C.secText,
                }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        )}

        {/* CTAs */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginTop: 'auto',
            zIndex: 1,
          }}
        >
          <GlowBtn
            size="xl"
            fullWidth
            onClick={() => {
              if (canProceed) onNavigate(5) /* → body selection */
            }}
            style={{
              fontSize: bp.isMobile ? '15px' : '17px',
              opacity: canProceed ? 1 : 0.35,
              pointerEvents: canProceed ? 'auto' : 'none',
              transition: 'opacity 0.3s ease, box-shadow 0.3s ease',
              boxShadow: canProceed ? `0 0 28px rgba(139,92,255,0.55)` : 'none',
            }}
          >
            KARAKTERİMİ OLUŞTUR →
          </GlowBtn>
          <GlowBtn size="lg" variant="secondary" fullWidth onClick={() => onNavigate(3)}>
            YENİDEN ÇEK
          </GlowBtn>
        </div>
      </div>
    </div>
  )
}

// ─── Screen 05 — Body Selection ───────────────────────────────────────────────

interface BodyOption {
  id: string // internal — never shown
  name: string // display name
  category: string
  accent: string
  gradient: string
}

// Seçenekler `outfits.ts`'ten gelir: backend'in kabul ettiği **ve** Unity'nin giydirebildiği
// formalar. Mock'un jenerik stil adları (KLASİK/ŞAMPİYON/…) hiçbir uçta karşılığı olmadığı
// için kullanılmadı — kiosk kontrat dışına çıkamaz.
const BODY_OPTIONS: BodyOption[] = SELECTABLE_OUTFITS.map((o) => ({
  id: o.id,
  name: o.label,
  category: 'FORMA',
  accent: o.color,
  // Zemin formanın **ikinci rengini** blok olarak kullanırdı; kartta emoji varken sorun değildi
  // ama forma sanatı gelince zemin formayla aynı renkleri paylaşıp onu yuttu (#116) — en kötüsü
  // beyaz zemindeki kırmızı-beyaz forma. Renk artık koyu tabana **vurgu** olarak biniyor: kimlik
  // kenarlık, ışıma ve formanın kendisinde kalır, zemin hepsinde okunur.
  gradient: `linear-gradient(160deg, ${o.accent}44 0%, #131024 55%, #0d0a18 100%)`,
}))

const UNSELECTED_BODY: BodyOption = {
  id: '',
  name: 'FORMANI SEÇ',
  category: 'FORMA',
  accent: '#8B5CFF',
  gradient: 'linear-gradient(160deg,#241a3d,#0d0a18)',
}

/**
 * Seçim ekranının forma resmi: `src/assets/bodies/{outfitId}.png` — oyunun **gerçek** forma sanatı,
 * `BodySprite`'ın gövde olarak çizdiği dosyanın aynısı (tarayıcı ikinci kez indirmez).
 *
 * Ayırt etme yükü buradadır (#116): etiket artık takımı söylemiyor, tarifsel bir renk adı.
 * Misafir kartı **görselden** tanır; beş kart aynı silüeti gösterirken tanıma yazıya yaslanıyordu.
 *
 * Kafa çizilmez: Ekran 05 fotoğraftan sonra ama üretimden önce gelir, o anda kafa sprite'ı
 * yoktur. Forma seçilmemişken (`UNSELECTED_BODY`, boş id) sanat yok — çizili kabuk kalır.
 */
function OutfitArt({ outfitId, accent }: { outfitId: string; accent: string }) {
  // Ölçü kaptan gelir: aynı bileşen 100px'lik mobil kartta da 180px'lik büyük önizlemede de
  // çizilir. Sabit piksel verilse biri taşar, öteki kutunun içinde küçük kalırdı.
  const box = { flex: 1, width: '100%', minHeight: 0 } as const
  const art = outfitId ? bodyArtUrl(outfitId) : null
  if (!art || !hasBodyArt(outfitId)) {
    return (
      <div
        style={{
          ...box,
          margin: '0 auto',
          maxWidth: '62%',
          borderRadius: '10px 10px 6px 6px',
          border: `1.5px dashed ${accent}55`,
          background: `linear-gradient(180deg, ${accent}22 0%, ${accent}0d 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '26px',
          opacity: 0.8,
        }}
      >
        👕
      </div>
    )
  }
  return (
    <img
      src={art}
      alt=""
      style={{
        ...box,
        objectFit: 'contain',
        filter: `drop-shadow(0 0 12px ${accent}55)`,
        pointerEvents: 'none',
      }}
    />
  )
}

function BodyCard({
  body,
  selected,
  onClick,
}: {
  body: BodyOption
  selected: boolean
  onClick: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const active = selected || hovered
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        cursor: 'pointer',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        background: body.gradient,
        border: `2px solid ${selected ? body.accent : active ? body.accent + '77' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: '18px',
        padding: '0',
        transform: selected ? 'scale(1.04)' : hovered ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.2s cubic-bezier(.34,1.56,.64,1)',
        boxShadow: selected
          ? `0 0 32px ${body.accent}66, 0 0 8px ${body.accent}44, inset 0 0 20px ${body.accent}11`
          : hovered
            ? `0 0 16px ${body.accent}33`
            : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Selected badge */}
      {selected && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            // Rozet formanın ana rengiyle dolu, tik beyazdı: sarı/beyaz formalarda tik
            // görünmüyordu. Renk artık **tikte**, zemin koyu — beş formada da okunur.
            background: '#0d0a18',
            border: `1.5px solid ${body.accent}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
            fontWeight: 900,
            color: body.accent,
            boxShadow: `0 0 10px ${body.accent}88`,
            zIndex: 2,
          }}
        >
          ✓
        </div>
      )}

      {/* Body preview area */}
      <div
        style={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 12px 8px',
          gap: '8px',
          background: `radial-gradient(ellipse at 50% 80%, ${body.accent}15 0%, transparent 60%)`,
        }}
      >
        <OutfitArt outfitId={body.id} accent={body.accent} />
      </div>

      {/* Label */}
      <div
        style={{
          width: '100%',
          padding: '8px 10px 12px',
          textAlign: 'center',
          borderTop: `1px solid ${body.accent}22`,
          background: 'rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            fontSize: '13px',
            fontWeight: 900,
            color: selected ? body.accent : '#fff',
            fontFamily: "'Exo 2',sans-serif",
            // Etiket artık iki kelime (#116). Masaüstü kartında (~155px) tek satırda kalsın diye
            // aralık kısıldı; 100px'lik mobil kartta boşluktan sararak iki satır olur — beş
            // seçeneğin hepsi iki kelime olduğu için sarma da hepsinde aynı.
            letterSpacing: '0.06em',
            lineHeight: 1.2,
            textShadow: selected ? `0 0 12px ${body.accent}` : 'none',
          }}
        >
          {body.name}
        </div>
        {selected && (
          <div
            style={{
              fontSize: '9px',
              fontWeight: 800,
              color: body.accent,
              letterSpacing: '0.18em',
              marginTop: '2px',
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            SEÇİLDİ
          </div>
        )}
      </div>
    </button>
  )
}

function Screen05BodySelect({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { outfitId, setOutfitId } = useKiosk()
  const selected = outfitId
  const setSelected = (id: string) => setOutfitId(id as OutfitId)
  // Seçim yapılmadan önizlemeye geçilmez; sessiz varsayılan forma yok (#27). Seçilmeden önce
  // panel bir takım göstermez — "FORMANI SEÇ" der.
  const selectedBody = BODY_OPTIONS.find((b) => b.id === selected) ?? UNSELECTED_BODY

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        background: '#060410',
        display: bp.isMobile ? 'flex' : 'grid',
        flexDirection: bp.isMobile ? 'column' : undefined,
        gridTemplateColumns: bp.isMobile ? undefined : '1fr clamp(320px,36%,420px)',
      }}
    >
      <Stars count={40} />

      {/* ── LEFT — Large preview ────────────────────────── */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: bp.isMobile ? '20px 20px 0' : '40px 48px',
          borderRight: bp.isMobile ? 'none' : '1px solid rgba(139,92,255,0.12)',
          zIndex: 2,
        }}
      >
        {/* Accent glow bg */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `radial-gradient(ellipse at 50% 60%, ${selectedBody.accent}10 0%, transparent 60%)`,
            transition: 'background 0.5s',
          }}
        />

        <div
          style={{ textAlign: 'center', marginBottom: bp.isMobile ? '16px' : '28px', zIndex: 2 }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9px',
              fontWeight: 700,
              color: selectedBody.accent,
              letterSpacing: '0.22em',
              marginBottom: '8px',
              opacity: 0.9,
            }}
          >
            ⚡ GÖRÜNÜM SEÇ
          </div>
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: bp.isMobile ? '24px' : 'clamp(26px,3vw,40px)',
              color: '#fff',
              letterSpacing: '0.04em',
              lineHeight: 1.1,
            }}
          >
            STİLİNİ SEÇ
          </div>
          <div style={{ fontSize: '14px', color: '#94A3B8', marginTop: '8px' }}>
            Oyundaki karakterinin vücudunu seç.
          </div>
        </div>

        {/* Large body preview */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: bp.isMobile ? '130px' : '180px',
            height: bp.isMobile ? '170px' : '240px',
            background: selectedBody.gradient,
            border: `2px solid ${selectedBody.accent}55`,
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            // Alt pay isim şeridine ait: forma resmi kabı doldurduğu için (flex:1) pay
            // ayrılmazsa yazı formanın ayakkabılarının üstüne binerdi.
            padding: bp.isMobile ? '12px 12px 34px' : '16px 16px 44px',
            boxShadow: `0 0 60px ${selectedBody.accent}33, 0 0 120px ${selectedBody.accent}18`,
            transition: 'all 0.4s ease',
            marginBottom: bp.isMobile ? '16px' : '24px',
          }}
        >
          <OutfitArt outfitId={selectedBody.id} accent={selectedBody.accent} />
          {/* Name overlay */}
          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: bp.isMobile ? '14px' : '17px',
              fontWeight: 900,
              color: selectedBody.accent,
              fontFamily: "'Exo 2',sans-serif",
              letterSpacing: '0.06em',
              lineHeight: 1.15,
              padding: '0 8px',
              textShadow: `0 0 16px ${selectedBody.accent}`,
            }}
          >
            {selectedBody.name}
          </div>
        </div>

        {/* CTA */}
        {!bp.isMobile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              width: '100%',
              maxWidth: '320px',
              zIndex: 2,
            }}
          >
            <GlowBtn
              size="xl"
              fullWidth
              onClick={() => {
                if (selected) onNavigate(6)
              }}
              style={{
                opacity: selected ? 1 : 0.35,
                pointerEvents: selected ? 'auto' : 'none',
              }}
            >
              {selected ? 'BU FORMAYLA DEVAM ET →' : 'ÖNCE FORMA SEÇ'}
            </GlowBtn>
            <GlowBtn size="md" variant="ghost" fullWidth onClick={() => onNavigate(4)}>
              ← GERİ
            </GlowBtn>
          </div>
        )}
      </div>

      {/* ── RIGHT — Body cards ──────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: bp.isMobile ? '0 16px 16px' : '32px 28px',
          gap: '12px',
          overflowY: 'auto',
          zIndex: 2,
        }}
      >
        {!bp.isMobile && (
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9px',
              fontWeight: 700,
              color: '#94A3B8',
              letterSpacing: '0.18em',
              marginBottom: '4px',
            }}
          >
            MEVCUT STİLLER
          </div>
        )}

        {bp.isMobile ? (
          // Mobile: horizontal scroll row
          <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px' }}>
            {BODY_OPTIONS.map((body) => (
              <div key={body.id} style={{ flexShrink: 0, width: '100px', height: '140px' }}>
                <BodyCard
                  body={body}
                  selected={selected === body.id}
                  onClick={() => setSelected(body.id)}
                />
              </div>
            ))}
          </div>
        ) : (
          // Desktop: 2-col grid
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', flex: 1 }}>
            {BODY_OPTIONS.map((body) => (
              <BodyCard
                key={body.id}
                body={body}
                selected={selected === body.id}
                onClick={() => setSelected(body.id)}
              />
            ))}
          </div>
        )}

        {bp.isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
            <GlowBtn
              size="lg"
              fullWidth
              onClick={() => {
                if (selected) onNavigate(6)
              }}
              style={{
                opacity: selected ? 1 : 0.35,
                pointerEvents: selected ? 'auto' : 'none',
              }}
            >
              {selected ? 'BU FORMAYLA DEVAM ET →' : 'ÖNCE FORMA SEÇ'}
            </GlowBtn>
            <GlowBtn size="sm" variant="ghost" fullWidth onClick={() => onNavigate(4)}>
              ← GERİ
            </GlowBtn>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Screen 06 — Character Preview ────────────────────────────────────────────

function Screen06CharPreview({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { capture, displayName, outfitId } = useKiosk()

  // Burada henüz üretim yok: misafir **gireni** görür (kendi fotosu + seçtiği forma),
  // üretilen kafa Screen07'de gelir. Sahte "AI kafa hazır" animasyonu kaldırıldı.
  const body = BODY_OPTIONS.find((b) => b.id === outfitId) ?? BODY_OPTIONS[0]!

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        background: `radial-gradient(ellipse at 50% 30%, #2D1B69 0%, #140D26 55%, #060410 100%)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        padding: bp.isMobile ? '24px 20px' : '40px 48px',
      }}
    >
      <Stars count={60} />
      <Particles count={14} color={body.accent} />

      <div style={{ textAlign: 'center', zIndex: 2 }}>
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '9px',
            fontWeight: 700,
            color: body.accent,
            letterSpacing: '0.22em',
            marginBottom: '8px',
            opacity: 0.85,
          }}
        >
          ✦ ÖNIZLEME
        </div>
        <div
          style={{
            fontFamily: "'Exo 2',sans-serif",
            fontWeight: 900,
            fontSize: bp.isMobile ? '22px' : 'clamp(24px,2.8vw,36px)',
            color: '#fff',
            letterSpacing: '0.04em',
            lineHeight: 1.1,
          }}
        >
          BÖYLE
          <br />
          <span style={{ color: body.accent }}>GÖRÜNECEKSİN</span>
        </div>
      </div>

      {/* Seçilen formanın **gerçek** gövdesi + misafirin çekilen karesi (kafa henüz üretilmedi) */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'flex-end',
          gap: '18px',
          filter: `drop-shadow(0 0 40px ${body.accent}44)`,
        }}
      >
        <div
          style={{
            width: bp.isMobile ? '84px' : '112px',
            height: bp.isMobile ? '84px' : '112px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            border: `2px solid ${body.accent}`,
            overflow: 'hidden',
            boxShadow: `0 0 30px ${body.accent}55`,
            flexShrink: 0,
          }}
        >
          {capture && (
            <img
              src={capture.url}
              alt="Çekilen portre"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </div>

        <CharacterFigure
          outfitId={body.id}
          color={body.accent}
          headUrl={null}
          size={bp.isMobile ? 110 : 150}
        />
      </div>

      {/* Name */}
      <div style={{ textAlign: 'center', zIndex: 2 }}>
        <div
          style={{
            fontSize: bp.isMobile ? '20px' : '26px',
            fontWeight: 900,
            color: '#fff',
            fontFamily: "'Exo 2',sans-serif",
            letterSpacing: '0.06em',
          }}
        >
          {displayName || 'OYUNCU'}
        </div>
        <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '4px' }}>
          Seçilen forma: <span style={{ color: body.accent, fontWeight: 700 }}>{body.name}</span>
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          width: '100%',
          maxWidth: '340px',
          zIndex: 2,
        }}
      >
        <GlowBtn size="xl" fullWidth onClick={() => onNavigate(7)}>
          KARAKTERİMİ OLUŞTUR ✨
        </GlowBtn>
        <GlowBtn size="md" variant="ghost" fullWidth onClick={() => onNavigate(5)}>
          STİLİ DEĞİŞTİR
        </GlowBtn>
      </div>
    </div>
  )
}

/** Takip QR'ı sunucuda üretilir (`segno`, ADR-012); kiosk yalnız gösterir. */
function TrackQr({ base64, size }: { base64: string; size: number }) {
  return (
    <img
      src={`data:image/png;base64,${base64}`}
      alt="Takip QR kodu"
      width={size}
      height={size}
      style={{
        display: 'block',
        borderRadius: '12px',
        background: '#fff',
        imageRendering: 'pixelated',
      }}
    />
  )
}

// ─── Screen 07 — AI Processing ────────────────────────────────────────────────
function Screen07({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { created, status, failureReason, submit, submitError } = useKiosk()
  const TIMEOUT = 12
  const [seconds, setSeconds] = useState(TIMEOUT)
  const submitted = useRef(false)

  // İstek tam burada gider: `POST /api/characters` → 202 QUEUED + takip QR (docs/api.md).
  // Ekran iki kez monte olsa da (StrictMode) tek POST: paralı üretim çift tetiklenmez.
  useEffect(() => {
    if (submitted.current) return
    submitted.current = true
    void submit()
  }, [submit])

  // Sayaç yalnız istek gittikten sonra işler; hata varsa misafir ekranda kalır
  useEffect(() => {
    if (!created) return
    const iv = setInterval(
      () =>
        setSeconds((s) => {
          if (s <= 1) {
            clearInterval(iv)
            return 0
          }
          return s - 1
        }),
      1000,
    )
    return () => clearInterval(iv)
  }, [created])

  useEffect(() => {
    if (status === 'READY') onNavigate(8)
  }, [status, onNavigate])

  // Süre dolduysa kiosk bir sonraki misafire açılır; üretim arkada sürer, misafir telefondan izler
  useEffect(() => {
    if (created && seconds === 0 && status !== 'READY' && status !== 'FAILED') onNavigate(1)
  }, [created, seconds, status, onNavigate])

  const failed = status === 'FAILED' || submitError !== null
  const progress = seconds / TIMEOUT
  const circumference = 2 * Math.PI * 28

  const pipeline = [
    { label: 'Fotoğraf alındı', state: created ? ('done' as const) : ('active' as const) },
    {
      label: 'Arcade dönüşümü',
      state:
        status === 'GENERATING'
          ? ('active' as const)
          : status === 'NORMALIZING' || status === 'READY'
            ? ('done' as const)
            : ('pending' as const),
    },
    {
      label: 'Oyuna hazırlanıyor',
      state:
        status === 'NORMALIZING'
          ? ('active' as const)
          : status === 'READY'
            ? ('done' as const)
            : ('pending' as const),
    },
  ]

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        background: '#060410',
        display: bp.isMobile ? 'flex' : 'grid',
        flexDirection: bp.isMobile ? 'column' : undefined,
        gridTemplateColumns: bp.isMobile ? undefined : '1fr clamp(240px,28%,300px)',
      }}
    >
      <Stars count={50} />

      {/* ── LEFT/CENTER — QR area ──────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: bp.isMobile ? '28px 20px 20px' : '40px 48px',
          gap: '20px',
          position: 'relative',
          zIndex: 2,
          borderRight: bp.isMobile ? 'none' : '1px solid rgba(139,92,255,0.12)',
        }}
      >
        {/* Heading */}
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9px',
              fontWeight: 700,
              color: C.neonPurple,
              letterSpacing: '0.2em',
              marginBottom: '10px',
              opacity: 0.8,
            }}
          >
            ⚡ İŞLEM BAŞLADI
          </div>
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: bp.isMobile ? '26px' : 'clamp(28px,3.2vw,42px)',
              color: C.white,
              lineHeight: 1.1,
              letterSpacing: '0.04em',
            }}
          >
            {failed ? (
              <>
                ÇEKİM
                <br />
                <span style={{ color: C.magenta }}>OLMADI</span>
              </>
            ) : (
              <>
                KARAKTERİN
                <br />
                <span style={{ color: C.neonPurple }}>OLUŞUYOR!</span>
              </>
            )}
          </div>
          <div
            style={{
              fontSize: '14px',
              color: failed ? C.magenta : C.secText,
              marginTop: '10px',
              lineHeight: 1.6,
            }}
          >
            {failed ? (failureReason ?? submitError) : 'İşlemi telefonundan takip edebilirsin.'}
          </div>
          {failed && (
            <div style={{ marginTop: '16px' }}>
              <GlowBtn size="lg" onClick={() => onNavigate(3)}>
                YENİDEN ÇEK 📸
              </GlowBtn>
            </div>
          )}
        </div>

        {/* QR block */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '14px',
            padding: bp.isMobile ? '20px' : '28px 32px',
            borderRadius: '20px',
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${C.neonPurple}33`,
            boxShadow: `0 0 40px ${C.neonPurple}18, inset 0 0 28px ${C.neonPurple}08`,
          }}
        >
          <div
            style={{
              fontFamily: "'Exo 2',sans-serif",
              fontWeight: 900,
              fontSize: '13px',
              color: C.white,
              letterSpacing: '0.14em',
            }}
          >
            QR KODU OKUT
          </div>

          {/* QR with ring glow */}
          <div style={{ position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                inset: '-14px',
                borderRadius: '20px',
                border: `1.5px solid ${C.neonPurple}33`,
                boxShadow: `0 0 24px ${C.neonPurple}22`,
                animation: 'glow-pulse 2s ease-in-out infinite',
              }}
            />
            <GlassPanel
              style={{
                padding: '16px',
                border: `2px solid ${C.neonPurple}55`,
                borderRadius: '16px',
              }}
            >
              {created ? (
                <TrackQr base64={created.qrPngBase64} size={bp.isMobile ? 150 : 190} />
              ) : (
                <div
                  style={{
                    width: bp.isMobile ? 150 : 190,
                    height: bp.isMobile ? 150 : 190,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      border: `3px solid ${C.neonPurple}44`,
                      borderTopColor: C.neonPurple,
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                </div>
              )}
            </GlassPanel>
          </div>

          <div
            style={{
              fontSize: '12px',
              color: C.secText,
              textAlign: 'center',
              maxWidth: '240px',
              lineHeight: 1.6,
            }}
          >
            Karakterin hazır olduğunda
            <br />
            telefonunda göreceksin.
          </div>

          {/* Player badge */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '999px',
              background: `${C.cyan}14`,
              border: `1px solid ${C.cyan}44`,
            }}
          >
            <div
              style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: '#00E676',
                boxShadow: '0 0 6px #00E676',
                animation: 'glow-pulse 1.4s ease-in-out infinite',
              }}
            />
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '13px',
                fontWeight: 700,
                color: C.cyan,
                letterSpacing: '0.1em',
              }}
            >
              PLAYER {created ? `#${created.characterId.slice(-3)}` : '…'}
            </span>
          </div>
        </div>
      </div>

      {/* ── RIGHT — Pipeline + timer ───────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: bp.isMobile ? '0 20px 24px' : '40px 28px',
          gap: '16px',
          background: 'linear-gradient(180deg, #0A0616 0%, #060410 100%)',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* Pipeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {pipeline.map((s, i) => {
            const isDone = s.state === 'done'
            const isActive = s.state === 'active'
            const dotColor = isDone ? '#00E676' : isActive ? C.neonPurple : `${C.secText}44`
            const lineColor = isDone ? 'rgba(0,230,118,0.3)' : 'rgba(139,92,255,0.12)'
            return (
              <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'stretch' }}>
                {/* Dot + connector line */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                    width: '20px',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: isDone
                        ? 'rgba(0,230,118,0.18)'
                        : isActive
                          ? `${C.neonPurple}22`
                          : 'rgba(255,255,255,0.06)',
                      border: `2px solid ${dotColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.4s ease',
                      boxShadow: isActive
                        ? `0 0 10px ${C.neonPurple}55`
                        : isDone
                          ? '0 0 8px rgba(0,230,118,0.4)'
                          : 'none',
                      animation: isActive ? 'glow-pulse 1.2s ease-in-out infinite' : 'none',
                      marginTop: '16px',
                    }}
                  >
                    {isDone && (
                      <svg width="9" height="8" viewBox="0 0 9 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L8 1"
                          stroke="#00E676"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {isActive && (
                      <div
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: C.neonPurple,
                        }}
                      />
                    )}
                  </div>
                  {i < pipeline.length - 1 && (
                    <div
                      style={{
                        width: '2px',
                        flex: 1,
                        background: lineColor,
                        minHeight: '20px',
                        transition: 'background 0.4s ease',
                      }}
                    />
                  )}
                </div>

                {/* Label */}
                <div
                  style={{
                    paddingTop: '14px',
                    paddingBottom: i < pipeline.length - 1 ? '14px' : '0',
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: isDone || isActive ? 700 : 500,
                      color: isDone ? '#00E676' : isActive ? C.white : `${C.secText}66`,
                      transition: 'color 0.4s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {s.label}
                    {isActive && (
                      <span style={{ display: 'inline-flex', gap: '3px' }}>
                        {[0, 1, 2].map((j) => (
                          <span
                            key={j}
                            style={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              background: C.neonPurple,
                              display: 'inline-block',
                              animation: `glow-pulse 1s ${j * 0.22}s ease-in-out infinite`,
                            }}
                          />
                        ))}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: 'rgba(139,92,255,0.12)' }} />

        {/* Countdown ring + CTA */}
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}
        >
          {/* Ring timer */}
          <div style={{ position: 'relative', width: '72px', height: '72px' }}>
            <svg
              width="72"
              height="72"
              style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
            >
              <circle
                cx="36"
                cy="36"
                r="28"
                fill="none"
                stroke="rgba(139,92,255,0.15)"
                strokeWidth="4"
              />
              <circle
                cx="36"
                cy="36"
                r="28"
                fill="none"
                stroke={seconds <= 3 ? C.magenta : C.neonPurple}
                strokeWidth="4"
                strokeDasharray={`${circumference}`}
                strokeDashoffset={`${circumference * (1 - progress)}`}
                strokeLinecap="round"
                style={{
                  transition: 'stroke-dashoffset 1s linear, stroke 0.4s ease',
                  filter: `drop-shadow(0 0 4px ${seconds <= 3 ? C.magenta : C.neonPurple})`,
                }}
              />
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 700,
                fontSize: '22px',
                color: seconds <= 3 ? C.magenta : C.white,
                transition: 'color 0.4s ease',
              }}
            >
              {seconds}
            </div>
          </div>

          <div
            style={{
              fontSize: '11px',
              color: `${C.secText}88`,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {seconds} saniye sonra ana ekrana dönülecek.
          </div>

          <GlowBtn
            size="xl"
            fullWidth
            onClick={() => onNavigate(1)}
            style={{ fontSize: '16px', boxShadow: `0 0 22px rgba(139,92,255,0.5)` }}
          >
            TAMAMDIR ✓
          </GlowBtn>
        </div>
      </div>
    </div>
  )
}

// ─── Screen 06 — Character Preview ───────────────────────────────────────────
function Screen08({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { created, displayName, outfitId, previewUrl } = useKiosk()
  const style = outfitStyle(outfitId ?? 'NEUTRAL')
  const char = { color: style.color }
  const playerNo = created ? `#${created.characterId.slice(-3)}` : '#—'

  const layout: CSSProperties = bp.isMobile
    ? { display: 'flex', flexDirection: 'column' }
    : { display: 'grid', gridTemplateColumns: 'clamp(260px,24%,310px) 1fr clamp(260px,24%,310px)' }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(ellipse at 50% 30%, #2D1B69 0%, #140D26 55%, ${C.deepBlack} 100%)`,
        ...layout,
      }}
    >
      <Stars count={50} />
      <Particles count={12} color={C.neonPurple} />

      {/* LEFT — Player card */}
      <SidePanel
        border="right"
        style={{ padding: bp.isMobile ? '20px' : '40px 28px', justifyContent: 'center', zIndex: 2 }}
      >
        <GlassPanel
          style={{ padding: '20px', border: `1px solid ${char.color}44` }}
          glow={char.color}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${char.color}44, ${C.primaryPurple})`,
                border: `2px solid ${char.color}88`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                boxShadow: `0 0 18px ${char.color}55`,
                overflow: 'hidden',
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={displayName}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                '⚽'
              )}
            </div>
            <div>
              <PlayerBadge id={playerNo} size="md" />
              <div style={{ fontSize: '18px', fontWeight: 900, color: C.white, marginTop: '6px' }}>
                {displayName || 'OYUNCU'}
              </div>
            </div>
            <StatusBadge variant="success">Karakter Hazır ⚽</StatusBadge>
          </div>
        </GlassPanel>

        <GlassPanel style={{ padding: '16px 18px' }}>
          <div
            style={{
              fontSize: '10px',
              fontWeight: 700,
              color: C.secText,
              letterSpacing: '0.12em',
              marginBottom: '10px',
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            KARAKTERİN ÖZELLİKLERİ
          </div>
          {[
            { label: 'AI Karikatür Kafa', val: '✓ Oluşturuldu' },
            { label: 'Forma', val: style.label },
            { label: 'Oyuncu No', val: playerNo },
          ].map(({ label, val }) => (
            <div
              key={label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '12px',
                padding: '5px 0',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <span style={{ color: C.secText }}>{label}</span>
              <span
                style={{
                  color: C.cyan,
                  fontWeight: 700,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: '11px',
                }}
              >
                {val}
              </span>
            </div>
          ))}
        </GlassPanel>
      </SidePanel>

      {/* CENTER — Character showcase */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '32px',
          zIndex: 2,
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '10px',
            color: C.secText,
            letterSpacing: '0.2em',
            marginBottom: '8px',
          }}
        >
          KARAKTERIN
        </div>
        <div style={{ animation: 'float-slow 3s ease-in-out infinite' }}>
          <CharacterFigure
            outfitId={outfitId}
            color={char.color}
            headUrl={previewUrl}
            size={bp.isMobile ? 160 : 220}
          />
        </div>
        <div
          style={{
            width: '140px',
            height: '18px',
            borderRadius: '50%',
            marginTop: '-8px',
            background: `radial-gradient(ellipse, ${char.color}55 0%, transparent 70%)`,
            animation: 'platform-glow 2.5s ease-in-out infinite',
            ['--gc' as string]: char.color,
          }}
        />
      </div>

      {/* RIGHT — Actions */}
      <SidePanel
        border="left"
        style={{ padding: bp.isMobile ? '20px' : '40px 28px', justifyContent: 'center', zIndex: 2 }}
      >
        <NeonText
          size={bp.isMobile ? 24 : 34}
          weight={900}
          color={C.white}
          style={{ lineHeight: 1.1 }}
        >
          İŞTE
          <br />
          <span style={{ color: C.neonPurple }}>SEN!</span>
        </NeonText>
        <p style={{ fontSize: '14px', color: C.secText, lineHeight: 1.7, margin: 0 }}>
          AI karikatür kafan futbol karakterine yerleştirildi. QR kodu okut, telefonundan takip et!
        </p>

        <GlassPanel style={{ padding: '16px 18px', textAlign: 'center' }}>
          <div
            style={{
              fontSize: '11px',
              color: C.secText,
              marginBottom: '4px',
              letterSpacing: '0.08em',
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            OYUNCU NUMARASI
          </div>
          <div
            style={{
              fontSize: '32px',
              fontWeight: 900,
              color: C.cyan,
              textShadow: `0 0 12px ${C.cyan}`,
            }}
          >
            ⚽ {playerNo}
          </div>
        </GlassPanel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto' }}>
          <GlowBtn size="xl" fullWidth onClick={() => onNavigate(9)} style={{ fontSize: '18px' }}>
            QR KODU AL 📱
          </GlowBtn>
        </div>
      </SidePanel>
    </div>
  )
}

// ─── Screen 09 — QR Handoff ───────────────────────────────────────────────────
function Screen09({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const { created, displayName, previewUrl } = useKiosk()
  const [seconds, setSeconds] = useState(12)

  useEffect(() => {
    const t = setInterval(
      () =>
        setSeconds((s) => {
          if (s <= 1) {
            clearInterval(t)
            return 0
          }
          return s - 1
        }),
      1000,
    )
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (seconds === 0) onNavigate(10)
  }, [seconds, onNavigate])

  const circumference = 2 * Math.PI * 60
  const stages = [
    { label: 'Portre alındı', state: 'done' as const },
    { label: 'AI kafa oluşturuldu', state: 'done' as const },
    { label: 'Karaktere yerleştirildi', state: 'done' as const },
    { label: "Mobil'e gönderiliyor", state: 'active' as const },
  ]

  const layout: CSSProperties = bp.isMobile
    ? { display: 'flex', flexDirection: 'column' }
    : { display: 'grid', gridTemplateColumns: 'clamp(260px,24%,300px) 1fr clamp(260px,24%,300px)' }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(ellipse at 50% 30%, #2D1B69 0%, #140D26 50%, ${C.deepBlack} 100%)`,
        ...layout,
      }}
    >
      <Stars count={50} />

      {/* LEFT */}
      <SidePanel
        border="right"
        style={{
          padding: bp.isMobile ? '20px' : '40px 28px',
          justifyContent: 'center',
          zIndex: 2,
          flexShrink: 0,
        }}
      >
        <GlassPanel style={{ padding: '18px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2D1B69, #1E1B4B)',
                border: `2px solid ${C.neonPurple}66`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={displayName}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                '⚽'
              )}
            </div>
            <div>
              <PlayerBadge id={created ? `#${created.characterId.slice(-3)}` : '#—'} size="md" />
              <div style={{ fontSize: '14px', fontWeight: 700, color: C.white, marginTop: '4px' }}>
                {displayName || 'OYUNCU'}
              </div>
            </div>
          </div>
          <StatusBadge variant="processing">Mobil'e gönderiliyor...</StatusBadge>
        </GlassPanel>

        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '52px',
              fontWeight: 700,
              color: C.cyan,
              textShadow: `0 0 14px ${C.cyan}`,
              lineHeight: 1,
            }}
          >
            {seconds}
          </div>
          <div
            style={{ fontSize: '11px', color: C.secText, letterSpacing: '0.1em', marginTop: '4px' }}
          >
            saniye sonra kapanır
          </div>
        </div>

        <p
          style={{
            fontSize: '12px',
            color: `${C.secText}77`,
            lineHeight: 1.6,
            margin: 0,
            textAlign: 'center',
          }}
        >
          QR okutulduğunda kiosk otomatik yeni ziyaretçi için hazır olur.
        </p>
        <GlowBtn size="md" variant="ghost" fullWidth onClick={() => onNavigate(10)}>
          Atla →
        </GlowBtn>
      </SidePanel>

      {/* CENTER — QR */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          padding: '40px',
          zIndex: 2,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <NeonText
            size={bp.isMobile ? 20 : 30}
            weight={900}
            color={C.neonPurple}
            style={{ letterSpacing: '0.08em', display: 'block', marginBottom: '6px' }}
          >
            TELEFONUNDAN DEVAM ET
          </NeonText>
          <p style={{ color: C.secText, fontSize: '14px', margin: 0 }}>
            QR kodu okut ve karakterini takip et.
          </p>
        </div>
        <div style={{ position: 'relative' }}>
          <svg
            width="280"
            height="280"
            style={{ position: 'absolute', top: '-20px', left: '-20px', pointerEvents: 'none' }}
          >
            <circle
              cx="140"
              cy="140"
              r="60"
              fill="none"
              stroke={`${C.neonPurple}22`}
              strokeWidth="4"
            />
            <circle
              cx="140"
              cy="140"
              r="60"
              fill="none"
              stroke={C.neonPurple}
              strokeWidth="4"
              strokeDasharray={`${circumference}`}
              strokeDashoffset={`${circumference * (1 - seconds / 12)}`}
              strokeLinecap="round"
              transform="rotate(-90,140,140)"
              style={{
                filter: `drop-shadow(0 0 6px ${C.neonPurple})`,
                transition: 'stroke-dashoffset 1s linear',
              }}
            />
          </svg>
          <GlassPanel
            style={{
              padding: '20px',
              border: `2px solid ${C.neonPurple}55`,
              boxShadow: `0 0 36px rgba(139,92,255,0.4)`,
            }}
          >
            {created && <TrackQr base64={created.qrPngBase64} size={bp.isMobile ? 180 : 220} />}
          </GlassPanel>
        </div>
      </div>

      {/* RIGHT — Timeline */}
      {!bp.isMobile && (
        <SidePanel
          border="left"
          style={{ padding: '40px 28px', justifyContent: 'center', zIndex: 2, flexShrink: 0 }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: 700,
              color: C.secText,
              letterSpacing: '0.1em',
              marginBottom: '4px',
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            İŞLEM DURUMU
          </div>
          <ProcessingTimeline stages={stages} />
          <GlassPanel style={{ padding: '14px 16px', marginTop: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: C.secText, lineHeight: 1.6 }}>
              Karakter <span style={{ color: C.cyan, fontWeight: 700 }}>mobil uygulamada</span>{' '}
              görünür oldu.
            </div>
          </GlassPanel>
        </SidePanel>
      )}
    </div>
  )
}

// ─── Screen 10 — Complete ─────────────────────────────────────────────────────
function Screen10({ onNavigate }: { onNavigate: (s: KioskScreen) => void }) {
  const bp = useBreakpoint()
  const [count, setCount] = useState(4)
  const { outfitId, previewUrl } = useKiosk()
  const char = { color: outfitStyle(outfitId ?? 'NEUTRAL').color }

  useEffect(() => {
    const t = setInterval(
      () =>
        setCount((c) => {
          if (c <= 1) {
            clearInterval(t)
            return 0
          }
          return c - 1
        }),
      1000,
    )
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (count === 0) onNavigate(1)
  }, [count, onNavigate])

  const layout: CSSProperties = bp.isMobile
    ? { display: 'flex', flexDirection: 'column' }
    : { display: 'grid', gridTemplateColumns: '1fr 1.3fr 1fr' }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(ellipse at 50% 40%, #2D1B69 0%, #140D26 60%, ${C.deepBlack} 100%)`,
        ...layout,
      }}
    >
      <Stars count={70} />
      <Particles count={24} color={C.neonPurple} />
      <Particles count={12} color={C.magenta} />
      {Array.from({ length: 28 }, (_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${(i / 28) * 100}%`,
            top: `${(i % 4) * 7}%`,
            width: `${6 + (i % 5)}px`,
            height: `${6 + (i % 6)}px`,
            background: ['#8B5CFF', '#35D8FF', '#F044C7', '#FFD700', '#00E676'][i % 5],
            borderRadius: i % 3 === 0 ? '50%' : '2px',
            ['--spin' as string]: `${i % 2 === 0 ? '' : '-'}${300 + i * 20}deg`,
            animation: `confetti-fall ${2.5 + (i % 3) * 0.5}s ${(i % 6) * 0.2}s ease forwards`,
            zIndex: 5,
          }}
        />
      ))}

      {/* LEFT — Football character */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: bp.isMobile ? '20px' : '40px',
          zIndex: 2,
          borderRight: !bp.isMobile ? '1px solid rgba(139,92,255,0.1)' : 'none',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div style={{ animation: 'celebrate-bounce 0.9s ease-in-out infinite' }}>
            <CharacterFigure
              outfitId={outfitId ?? 'NEUTRAL'}
              color={char.color}
              headUrl={previewUrl}
              size={bp.isMobile ? 150 : 200}
            />
          </div>
          <div
            style={{
              width: '130px',
              height: '16px',
              borderRadius: '50%',
              background: `radial-gradient(ellipse, ${char.color}55, transparent 70%)`,
              animation: 'platform-glow 2.5s ease-in-out infinite',
              ['--gc' as string]: char.color,
            }}
          />
        </div>
      </div>

      {/* CENTER — Success */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          padding: '40px',
          textAlign: 'center',
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #065F46, #00E676)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 48px #00E67688, 0 0 96px #00E67644',
            fontSize: '46px',
            animation: 'slide-up 0.4s ease both',
          }}
        >
          ✓
        </div>
        <NeonText
          size={bp.isMobile ? 44 : 68}
          weight={900}
          color={C.white}
          style={{ letterSpacing: '0.12em', animation: 'slide-up 0.4s 0.1s ease both' }}
        >
          TAMAMDIR!
        </NeonText>
        <p
          style={{
            color: C.secText,
            fontSize: '16px',
            margin: 0,
            animation: 'slide-up 0.4s 0.2s ease both',
            lineHeight: 1.6,
          }}
        >
          Karikatür kafan hazırlandı.
          <br />
          Telefonundan takip edebilirsin.
        </p>
        <GlassPanel style={{ padding: '12px 22px' }}>
          <div style={{ fontSize: '13px', color: C.secText }}>
            📱 Mobil uygulamayı aç → Karakterini gör
          </div>
        </GlassPanel>
      </div>

      {/* RIGHT — Countdown */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '40px',
          zIndex: 2,
          borderLeft: !bp.isMobile ? '1px solid rgba(139,92,255,0.1)' : 'none',
        }}
      >
        <GlassPanel style={{ padding: '24px 32px', textAlign: 'center', width: '100%' }}>
          <div
            style={{
              fontSize: '13px',
              color: C.secText,
              marginBottom: '12px',
              letterSpacing: '0.06em',
            }}
          >
            Yeni ziyaretçi için hazırlanıyor…
          </div>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 'clamp(52px,5vw,76px)',
              fontWeight: 700,
              color: C.neonPurple,
              textShadow: `0 0 16px ${C.neonPurple}`,
              lineHeight: 1,
            }}
          >
            {count}
          </div>
          <div style={{ fontSize: '12px', color: C.secText, marginTop: '10px' }}>
            saniye sonra başa dönüyor
          </div>
        </GlassPanel>
      </div>
    </div>
  )
}

// ─── Step Progress Bar ─────────────────────────────────────────────────────────
const STEPS = ['PORTRE', 'İSİM', 'STİL', 'ÖNIZLEME', 'QR']
function stepFromScreen(s: KioskScreen): number {
  if (s <= 2) return 0
  if (s === 3) return 1
  if (s === 4) return 2
  if (s === 5) return 3
  if (s === 6) return 4
  if (s === 7) return 4
  return 5
}

export function StepProgress({ screen }: { screen: KioskScreen }) {
  const bp = useBreakpoint()
  if (screen === 1) return null
  const active = stepFromScreen(screen)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: bp.isMobile ? '4px' : '8px' }}>
      {STEPS.map((label, i) => {
        const isDone = i < active
        const isCurrent = i === active - 1
        return (
          <React.Fragment key={i}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: bp.isMobile ? '5px 8px' : '6px 14px',
                borderRadius: '999px',
                background: isCurrent
                  ? `linear-gradient(135deg, ${C.primaryPurple}, ${C.neonPurple})`
                  : isDone
                    ? 'rgba(0,230,118,0.12)'
                    : 'rgba(170,165,191,0.08)',
                border: isCurrent
                  ? `1px solid ${C.neonPurple}`
                  : isDone
                    ? '1px solid rgba(0,230,118,0.4)'
                    : '1px solid rgba(170,165,191,0.15)',
                boxShadow: isCurrent ? `0 0 12px ${C.neonPurple}88` : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              <span
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: isCurrent
                    ? 'rgba(255,255,255,0.2)'
                    : isDone
                      ? '#00E67622'
                      : 'rgba(170,165,191,0.12)',
                  border: isCurrent
                    ? '1.5px solid rgba(255,255,255,0.4)'
                    : isDone
                      ? '1.5px solid #00E676'
                      : '1.5px solid rgba(170,165,191,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: isDone ? '#00E676' : isCurrent ? C.white : C.secText,
                  flexShrink: 0,
                }}
              >
                {isDone ? '✓' : i + 1}
              </span>
              {!bp.isMobile && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: isCurrent ? C.white : isDone ? '#00E676' : C.secText,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  maxWidth: bp.isMobile ? '8px' : '24px',
                  height: '1px',
                  background: i < active - 1 ? 'rgba(0,230,118,0.4)' : 'rgba(170,165,191,0.2)',
                }}
              />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

// ─── Kiosk App ─────────────────────────────────────────────────────────────────
interface KioskAppProps {
  screen: KioskScreen
  onNavigate: (s: KioskScreen) => void
}

export default function KioskApp({ screen, onNavigate }: KioskAppProps) {
  const renderScreen = () => {
    switch (screen) {
      case 1:
        return <Screen01 onNavigate={onNavigate} />
      case 2:
        return <Screen02 onNavigate={onNavigate} />
      case 3:
        return <Screen03 onNavigate={onNavigate} />
      case 4:
        return <Screen04 onNavigate={onNavigate} />
      case 5:
        return <Screen05BodySelect onNavigate={onNavigate} />
      case 6:
        return <Screen06CharPreview onNavigate={onNavigate} />
      case 7:
        return <Screen07 onNavigate={onNavigate} />
      case 8:
        return <Screen08 onNavigate={onNavigate} />
      case 9:
        return <Screen09 onNavigate={onNavigate} />
      case 10:
        return <Screen10 onNavigate={onNavigate} />
    }
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        animation: 'depth-in 0.35s ease both',
      }}
      key={screen}
    >
      {renderScreen()}
    </div>
  )
}
